import RegistrationModal from './RegistrationModal';
import ResultCard from './ResultCard';
import JevStrip from './JevStrip';
import { FEATURE_ACCESS } from '../config';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addLeadNote,
  assignLead,
  claimLead,
  confirmConsultation,
  createFollowup,
  fetchAppointmentAvailability,
  fetchLead,
  fetchLeadFollowups,
  fetchCallResult,
  fetchConversationReplySuggestion,
  fetchPendingDispositions,
  fetchTelecallers,
  fetchWaTemplates,
  fetchWhatsAppMessages,
  markWhatsAppRead,
  getTelecallerPhone,
  initCall,
  recordLeadMilestone,
  sendWhatsapp,
  sendWhatsAppFile,
  setTelecallerPhone,
  updateConsultationOutcome,
  updateFollowupStatus,
  updateLeadName,
  updateReportedSource,
  updateStatus,
  updateTier,
} from '../api';
import type {
  CallResultRecord,
  CallLog,
  CareMilestone,
  AppointmentSlot,
  ConsultationStage,
  Followup,
  Lead,
  LeadAttribution,
  LeadReportedSource,
  LeadEvent,
  LeadOutcome,
  LeadStatus,
  QualificationOutcome,
  QualificationOutcomeRecord,
  Telecaller,
  WaTemplate,
  WhatsAppMessageRecord,
  InteractionDisposition,
  ConversationAssist,
} from '../types';
import { CARE_MILESTONE_LABEL, CARE_MILESTONES, parseLeadTags, STATUSES, STATUS_COLOR, STATUS_LABEL } from '../types';
import { useWhatsAppWebSocket, type WhatsAppRealtimeEvent } from '../hooks/useWhatsAppWebSocket';
import { isMapConsultationLead, isMapLead, mapLeadDetails } from '../lib/lead-context';
import { leadContactPolicy } from '../../../shared/contact-policy';
import {
  CONTACTED_REASONS,
  LOST_REASONS,
  REJECTED_REASONS,
  QUALIFIED_REASON_CATEGORIES,
  QUALIFIED_REASON_OPTIONS_BY_CATEGORY,
  type QualifiedReasonCategory,
  type ReasonOption,
} from '../constants/leadReasons';

function waHref(phone: string, opening: string) {
  return `https://wa.me/${phone.replace(/[^\d]/g, '')}?text=${encodeURIComponent(opening)}`;
}

export function shouldSendWhatsAppKey(key: string, shiftKey: boolean): boolean {
  return key === 'Enter' && !shiftKey;
}

export function createOptimisticWhatsAppMessage(
  text: string,
  clientTempId: string,
  timestamp: string,
): WhatsAppMessageRecord {
  return {
    id: clientTempId,
    direction: 'outbound',
    provider_message_id: null,
    client_temp_id: clientTempId,
    text,
    type: 'text',
    timestamp,
    status: 'sending',
  };
}

export function markWhatsAppMessageFailed(
  messages: WhatsAppMessageRecord[],
  clientTempId: string,
): WhatsAppMessageRecord[] {
  return messages.map((message) => message.client_temp_id === clientTempId
    ? { ...message, status: 'failed' }
    : message);
}

export function friendlyWhatsAppSendError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? '');
  if (/session.*closed|template.*required|outside.*window/i.test(message)) {
    return 'The 24-hour session window has closed. A template message is required to reopen the conversation.';
  }
  if (/not configured|WATI/i.test(message)) {
    return 'WhatsApp sending is not configured on the server.';
  }
  return `Send failed: ${message}`;
}

function recordKey(message: WhatsAppMessageRecord): string {
  return message.provider_message_id || message.client_temp_id || message.id;
}

function isSameWhatsAppMessage(left: WhatsAppMessageRecord, right: WhatsAppMessageRecord): boolean {
  if (left.provider_message_id && right.provider_message_id) {
    return left.provider_message_id === right.provider_message_id;
  }
  if (left.client_temp_id && right.client_temp_id) {
    return left.client_temp_id === right.client_temp_id;
  }
  if (left.direction !== right.direction || left.text.trim() !== right.text.trim()) return false;
  const leftTime = Date.parse(left.timestamp);
  const rightTime = Date.parse(right.timestamp);
  return Number.isFinite(leftTime) && Number.isFinite(rightTime)
    && Math.abs(leftTime - rightTime) <= 5 * 1000;
}

export function mergeWhatsAppMessageRecords(
  current: WhatsAppMessageRecord[],
  incoming: WhatsAppMessageRecord,
): WhatsAppMessageRecord[] {
  const next = current.filter((message) => {
    return message.id !== incoming.id && !isSameWhatsAppMessage(message, incoming);
  });
  next.push(incoming);
  next.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  return next;
}

export function applyWhatsAppRealtimeEvent(
  current: WhatsAppMessageRecord[],
  event: WhatsAppRealtimeEvent,
): WhatsAppMessageRecord[] {
  if (event.type === 'NEW_MESSAGE') return mergeWhatsAppMessageRecords(current, event.message);
  return current.map((message) =>
    message.provider_message_id === event.provider_message_id
      || (event.client_temp_id && message.client_temp_id === event.client_temp_id)
      ? { ...message, status: event.status }
      : message,
  );
}

export function reconcileWhatsAppHistory(
  current: WhatsAppMessageRecord[],
  history: { provider: string; messages: WhatsAppMessageRecord[] },
): WhatsAppMessageRecord[] {
  return history.messages.reduce(
    (messages, message) => mergeWhatsAppMessageRecords(messages, message),
    current,
  );
}

export function shouldUseLegacyWhatsAppHistory(provider: string): boolean {
  return provider !== 'wati';
}

function fmtDuration(s: number) {
  if (!s) return '0s';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
}

const REPORTED_SOURCE_OPTIONS: Array<{ value: LeadReportedSource['category']; label: string }> = [
  { value: 'meta', label: 'Facebook / Instagram / Meta' },
  { value: 'google', label: 'Google' },
  { value: 'referral', label: 'Friend / family referral' },
  { value: 'doctor_or_clinic', label: 'Doctor / clinic referral' },
  { value: 'existing_patient', label: 'Existing patient / previous visit' },
  { value: 'offline', label: 'Outdoor / event / other offline' },
  { value: 'does_not_remember', label: 'Does not remember' },
  { value: 'other', label: 'Other' },
];

export function friendlyCallMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? '');
  if (/contact_restricted/i.test(message)) {
    return 'This contact has a contact restriction. Refresh the lead and follow the contact guidance before calling.';
  }
  if (/already in progress/i.test(message)) {
    return 'Please wait — a call is already in progress for this lead. Try again after the current call ends.';
  }
  if (/assigned to another agent|already assigned/i.test(message)) {
    return 'This lead is assigned to another health advisor. Please contact the assigned advisor or a manager if you need access.';
  }
  return 'We could not start the call right now. Please wait a moment and try again.';
}

export function friendlyAssignmentMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? '');
  if (/already assigned|assignment changed|assigned to another agent/i.test(message)) {
    return 'This lead was just assigned to another health advisor. The latest assignment has been refreshed.';
  }
  return 'We could not update the assignment. Please refresh and try again.';
}

function formatLocalDateInput(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function eventLabel(kind: string) {
  const map: Record<string, string> = {
    created: 'Lead created',
    inbound_call: 'Inbound call',
    call_initiated: 'Outbound call started',
    call_completed: 'Call ended',
    care_milestone: 'Care milestone',
    care_milestone_updated: 'Care milestone updated',
    status_change: 'Status changed',
    contact_attempt_disposition: 'Contact attempt recorded — lead status retained',
    whatsapp_sent: 'WhatsApp sent',
    assigned: 'Assigned',
    name_updated: 'Name updated',
    note: 'Note',
    followup_scheduled: 'Follow-up scheduled',
    followup_completed: 'Follow-up completed',
    followup_cancelled: 'Follow-up cancelled',
    consultation_outcome_changed: 'Consultation outcome updated',
    appointment_confirmed: 'Appointment confirmed',
  };
  return map[kind] ?? kind;
}

function findNeoDoveProperty(answers: Record<string, unknown>, key: string): unknown {
  const lists = Array.isArray(answers.other_properties) ? answers.other_properties : [];

  for (const list of lists) {
    if (!list || typeof list !== 'object') continue;
    const properties = (list as { properties?: unknown }).properties;
    if (!Array.isArray(properties)) continue;

    const match = properties.find((property) => {
      if (!property || typeof property !== 'object') return false;
      return (property as { name?: unknown }).name === key;
    });

    if (match && typeof match === 'object') {
      return (match as { value?: unknown }).value;
    }
  }

  return undefined;
}

function asDisplayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return '';
  if (typeof value === 'object') return '';
  return String(value);
}

function formatNeoDoveAnswers(rawAnswers: string | null) {
  if (!rawAnswers) return [];

  try {
    const answers = JSON.parse(rawAnswers) as Record<string, unknown>;
    const property = (key: string) => findNeoDoveProperty(answers, key);
    const rows: Array<[string, unknown]> = [
      ['Source', answers.source || answers.provider],
      ['Event', answers.event_name],
      ['Mobile', answers.mobile || answers.phone],
      ['Lead status', answers.lead_status_name || answers.lead_stage_name],
      ['Call status', property('status')],
      ['Direction', property('direction')],
      ['Virtual number', property('virtualnumber')],
      ['Time', property('timeFormatted') || answers.time_formatted],
      ['Agent', answers.agent_name],
      ['Campaign ID', answers.campaign_id],
      ['NeoDove lead ID', answers.lead_id],
    ];

    return rows
      .map(([label, value]) => ({ label, value: asDisplayValue(value) }))
      .filter((row) => row.value);
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Human-readable answers formatter
// ---------------------------------------------------------------------------

function parseStoredTimestamp(value: string): Date {
  const trimmed = value.trim();
  const sqliteUtc = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
  return new Date(sqliteUtc.test(trimmed) ? `${trimmed.replace(' ', 'T')}Z` : trimmed);
}

export function formatDateTime(iso: string): string {
  try {
    return parseStoredTimestamp(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  } catch {
    return iso;
  }
}

function labelCase(key: string): string {
  return key.trim()
    .replace(/[_\-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
}

type AnswerRow = { label: string; value: string };

type WhatsAppMessage = {
  message: string;
  messageId: string;
  timestamp: string;
  direction?: 'inbound' | 'outbound';
  status?: string | null;
};

type WhatsAppRecordContext = {
  source?: string | null;
  latestSource?: string | null;
  sourceHistory?: string | string[] | null;
  clinicTag?: string | null;
};

export type WhatsAppPresentation = {
  messages: WhatsAppMessage[];
  metadata: AnswerRow[];
};

function isUrlLike(value: string): boolean {
  return /^https?:\/\/\S+$/i.test(value.trim());
}

function describeMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return '';
  if (isUrlLike(trimmed)) {
    const lower = trimmed.toLowerCase();
    if (/\.(jpg|jpeg|png|gif|webp|bmp|heic)(\?.*)?$/.test(lower)) return `Image attachment\n${trimmed}`;
    if (/\.(mp4|mov|webm|avi|mkv)(\?.*)?$/.test(lower)) return `Video attachment\n${trimmed}`;
    if (/\.(pdf)(\?.*)?$/.test(lower)) return `Document attachment\n${trimmed}`;
    return `Link\n${trimmed}`;
  }
  if (/^-\s*https?:\/\/\S+$/i.test(trimmed)) {
    const url = trimmed.replace(/^-+\s*/, '');
    return describeMessage(url);
  }
  return trimmed;
}

function prettyLabel(key: string): string {
  return labelCase(key.trim().replace(/\s+/g, ' '));
}

function decodeHtmlEntities(value: string): string {
  if (!value || typeof document === 'undefined') return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function hasRenderableValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

function looksLikeDateKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return normalized === 'at'
    || normalized.endsWith('_at')
    || normalized.includes('timestamp')
    || normalized.includes('date')
    || normalized.includes('time');
}

function formatScalarValue(value: unknown, key = ''): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  const str = decodeHtmlEntities(String(value).trim());
  if (!str) return '';
  if (looksLikeDateKey(key)) {
    const formatted = formatDateTime(str);
    if (formatted !== str) return formatted;
  }
  if (isUrlLike(str) || /^-\s*https?:\/\/\S+$/i.test(str)) return describeMessage(str);
  return str;
}

function pathLabel(path: string[]): string {
  return path
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) return '';
      if (/^\d+$/.test(trimmed)) return `#${Number(trimmed)}`;
      return prettyLabel(trimmed);
    })
    .filter(Boolean)
    .join(' / ');
}

function sourceHistory(value: Lead['source_history']): string[] {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function historyValue(value: unknown, date = false): string {
  if (value === null || value === undefined || String(value).trim() === '') return '—';
  if (!date) return String(value);
  const parsed = new Date(String(value).replace(' ', 'T') + (String(value).includes('Z') ? '' : 'Z'));
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function historySourceLabel(source: string): string {
  return source.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

export function extractAgentNotes(events: LeadEvent[]) {
  return events.flatMap((event) => {
    if (event.kind !== 'note' || !event.data) return [];
    try {
      const data: unknown = JSON.parse(event.data);
      if (!isPlainObject(data) || typeof data.note !== 'string' || !data.note.trim()) return [];
      return [{ note: data.note, actor: event.actor, createdAt: event.created_at }];
    } catch {
      return [];
    }
  });
}

function pushDynamicRows(rows: AnswerRow[], value: unknown, path: string[] = []): void {
  if (!hasRenderableValue(value)) return;

  if (Array.isArray(value)) {
    if (value.length === 0) return;

    const allPrimitive = value.every((item) => !isPlainObject(item) && !Array.isArray(item));
    if (allPrimitive) {
      const joined = value
        .map((item) => formatScalarValue(item, path[path.length - 1] ?? ''))
        .filter(Boolean)
        .join('\n');
      if (joined) rows.push({ label: pathLabel(path) || 'Answers', value: joined });
      return;
    }

    value.forEach((item, index) => {
      if (isPlainObject(item) && typeof item.name === 'string' && Array.isArray(item.values) && item.values.length > 0) {
        const itemName = item.name;
        const rendered = item.values
          .map((entry) => formatScalarValue(entry, itemName))
          .filter(Boolean)
          .join('\n');
        if (rendered) rows.push({ label: itemName, value: rendered });
        return;
      }

      if (isPlainObject(item)) {
        pushDynamicRows(rows, item, [...path, String(index + 1)]);
        return;
      }

      const rendered = formatScalarValue(item, path[path.length - 1] ?? '');
      if (rendered) rows.push({ label: `${pathLabel(path) || 'Answers'} / #${index + 1}`, value: rendered });
    });
    return;
  }

  if (isPlainObject(value)) {
    for (const [key, child] of Object.entries(value)) {
      if (child === null || child === undefined || child === '') continue;
      if (key === '_extraction_source') continue;
      const nextPath = [...path, key];
      if (Array.isArray(child) || isPlainObject(child)) {
        pushDynamicRows(rows, child, nextPath);
        continue;
      }
      const rendered = formatScalarValue(child, key);
      if (rendered) rows.push({ label: pathLabel(nextPath), value: rendered });
    }
    return;
  }

  const rendered = formatScalarValue(value, path[path.length - 1] ?? '');
  if (rendered) rows.push({ label: pathLabel(path) || 'Answers', value: rendered });
}

function formatAnswersForDisplay(rawAnswers: string | null): AnswerRow[] {
  if (!rawAnswers) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeHtmlEntities(rawAnswers));
  } catch {
    return [{ label: 'Answers', value: decodeHtmlEntities(rawAnswers) }];
  }

  const rows: AnswerRow[] = [];
  if (isPlainObject(parsed)) {
    pushDynamicRows(rows, parsed);
  } else {
    pushDynamicRows(rows, parsed, ['Answers']);
  }

  return rows;
}

function isTechnicalMetaRow(label: string): boolean {
  const normalized = label.trim().toLowerCase();
  return (
    normalized === 'leadgen id'
    || normalized === 'page id'
    || normalized === 'form id'
    || normalized === 'data source'
    || normalized.startsWith('raw payload')
    || normalized.includes(' / raw payload / ')
    || normalized.includes('field data raw')
    || normalized.includes(' / #')
  );
}

function splitMetaRows(rows: AnswerRow[]) {
  const visible: AnswerRow[] = [];
  const hidden: AnswerRow[] = [];
  for (const row of rows) {
    if (isTechnicalMetaRow(row.label)) hidden.push(row);
    else visible.push(row);
  }
  return { visible, hidden };
}

function AnswersCard({ rows }: { rows: AnswerRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="mt-1 divide-y divide-slate-100 rounded-lg bg-white">
      {rows.map((row) => (
        <div key={row.label} className="flex items-start justify-between gap-3 px-2 py-1.5">
          <span className="shrink-0 text-[11px] text-slate-500">{row.label}</span>
          <span className="whitespace-pre-line break-words text-right text-[11px] font-medium text-slate-800">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function parseRawAnswers(rawAnswers: string | null): Record<string, unknown> | null {
  if (!rawAnswers) return null;
  try {
    const parsed: unknown = JSON.parse(decodeHtmlEntities(rawAnswers));
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function stringValue(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

function explicitDirection(value: unknown): WhatsAppMessage['direction'] {
  const direction = stringValue(value).toLowerCase();
  return direction === 'inbound' || direction === 'outbound' ? direction : undefined;
}

function messageFrom(value: unknown): WhatsAppMessage | null {
  if (!isPlainObject(value)) return null;
  const message = {
    message: stringValue(value.message),
    messageId: stringValue(value.message_id),
    timestamp: stringValue(value.timestamp),
    direction: explicitDirection(value.direction),
  };
  return message.message || message.messageId || message.timestamp || message.direction ? message : null;
}

function sameMessage(left: WhatsAppMessage, right: WhatsAppMessage): boolean {
  if (left.messageId && right.messageId) return left.messageId === right.messageId;
  return left.message === right.message && left.timestamp === right.timestamp;
}

function timestampValue(value: string): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

function isConversationRow(row: AnswerRow): boolean {
  const label = row.label.trim().toLowerCase();
  return label === 'message'
    || label === 'latest message'
    || label.startsWith('conversation history /');
}

function addMetadataFallback(rows: AnswerRow[], label: string, value: unknown): void {
  const rendered = formatScalarValue(value, label);
  if (!rendered || rows.some((row) => row.label.toLowerCase() === label.toLowerCase())) return;
  rows.push({ label, value: rendered });
}

export function isWhatsAppRecord(
  rawAnswers: string | null,
  context: WhatsAppRecordContext = {},
): boolean {
  const answers = parseRawAnswers(rawAnswers);
  const indicators = [
    context.source,
    context.latestSource,
    ...(Array.isArray(context.sourceHistory) ? context.sourceHistory : [context.sourceHistory]),
    answers?.source,
    answers?.provider,
    answers?.whatsapp_origin,
  ];
  return indicators.some((value) => stringValue(value).toLowerCase().includes('whatsapp'))
    || Array.isArray(answers?.conversation_history);
}

export function buildWhatsAppPresentation(
  rawAnswers: string | null,
  context: WhatsAppRecordContext = {},
): WhatsAppPresentation | null {
  if (!isWhatsAppRecord(rawAnswers, context)) return null;

  const answers = parseRawAnswers(rawAnswers);
  const history = Array.isArray(answers?.conversation_history)
    ? answers.conversation_history.map(messageFrom).filter((message): message is WhatsAppMessage => Boolean(message))
    : [];
  const current = messageFrom({
    message: answers?.latest_message ?? answers?.message,
    message_id: answers?.latest_message_id ?? answers?.message_id,
    timestamp: answers?.latest_timestamp ?? answers?.timestamp,
    direction: answers?.direction,
  });

  if (current && (history.length === 0 || !history.some((message) => sameMessage(message, current)))) {
    history.push(current);
  }
  if (history.length === 0) history.push({ message: '', messageId: '', timestamp: '' });

  const messages = history
    .map((message, index) => ({ message, index, timestamp: timestampValue(message.timestamp) }))
    .sort((left, right) => {
      if (left.timestamp === null && right.timestamp === null) return left.index - right.index;
      if (left.timestamp === null) return 1;
      if (right.timestamp === null) return -1;
      return left.timestamp - right.timestamp || left.index - right.index;
    })
    .map(({ message }) => message);

  const metadata = formatAnswersForDisplay(rawAnswers).filter((row) => !isConversationRow(row));
  addMetadataFallback(metadata, 'Source', context.latestSource || context.source);
  addMetadataFallback(metadata, 'Clinic tag', context.clinicTag);
  addMetadataFallback(
    metadata,
    'Source history',
    Array.isArray(context.sourceHistory) ? context.sourceHistory.join(', ') : context.sourceHistory,
  );

  return { messages, metadata };
}

function chatTimestamp(timestamp: string): string {
  if (!timestamp) return 'Timestamp unavailable';
  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? timestamp : parsed.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

export function WhatsAppConversationView({
  presentation,
  composer,
}: {
  presentation: WhatsAppPresentation;
  composer?: React.ReactNode;
}) {
  const [metadataOpen, setMetadataOpen] = useState(false);
  const messageListRef = useRef<HTMLOListElement>(null);
  const keepAtBottomRef = useRef(true);
  const newestMessage = presentation.messages.at(-1);

  useEffect(() => {
    const list = messageListRef.current;
    if (list && keepAtBottomRef.current) list.scrollTop = list.scrollHeight;
  }, [presentation.messages.length, newestMessage?.messageId, newestMessage?.status]);

  return (
    <div className="mt-3 space-y-3">
      <section aria-labelledby="whatsapp-conversation-title">
        <div id="whatsapp-conversation-title" className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          WhatsApp conversation
        </div>
        <div className="overflow-hidden rounded-xl border border-emerald-100 bg-emerald-50/60">
          <ol
            ref={messageListRef}
            aria-label="WhatsApp conversation"
            onScroll={(event) => {
              const list = event.currentTarget;
              keepAtBottomRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < 40;
            }}
            className="max-h-[28rem] space-y-2 overflow-y-auto p-3"
          >
            {presentation.messages.length === 0 && (
              <li className="py-6 text-center text-xs text-slate-500">No WhatsApp messages yet.</li>
            )}
            {presentation.messages.map((message, index) => {
              const outbound = message.direction === 'outbound';
              return (
                <li key={message.messageId || `${message.timestamp}-${index}`} className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[88%] rounded-2xl px-3 py-2 shadow-sm ${outbound ? 'rounded-br-sm bg-emerald-100' : 'rounded-tl-sm bg-white'}`}>
                    <p className="whitespace-pre-wrap break-words text-sm text-slate-800">
                      {message.message || 'Message unavailable'}
                    </p>
                    <time dateTime={message.timestamp || undefined} className="mt-1 block text-right text-[10px] text-slate-500">
                      {chatTimestamp(message.timestamp)}
                    </time>
                  </div>
                </li>
              );
            })}
          </ol>
          {composer && <div className="sticky bottom-0 border-t border-emerald-100 bg-white p-2">{composer}</div>}
        </div>
      </section>

      {presentation.metadata.length > 0 && (
        <section aria-labelledby="whatsapp-metadata-title">
          <button
            type="button"
            id="whatsapp-metadata-title"
            aria-expanded={metadataOpen}
            aria-controls="whatsapp-metadata-content"
            onClick={() => setMetadataOpen((open) => !open)}
            className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50"
          >
            <span>Message metadata</span>
            <span aria-hidden="true" className="text-base leading-none text-slate-400">
              {metadataOpen ? '−' : '+'}
            </span>
          </button>
          {metadataOpen && (
            <dl id="whatsapp-metadata-content" className="mt-2 grid grid-cols-1 gap-2 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-2">
              {presentation.metadata.map((row, index) => (
                <div key={`${row.label}-${index}`} className="min-w-0 rounded-lg bg-slate-50 px-3 py-2">
                  <dt className="text-[10px] uppercase tracking-wide text-slate-500">{row.label}</dt>
                  <dd className="mt-0.5 whitespace-pre-line break-words text-xs font-medium text-slate-800">{row.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>
      )}
    </div>
  );
}

export function WhatsAppComposer({
  value,
  busy,
  disabled,
  error,
  onChange,
  onSend,
  onFile,
}: {
  value: string;
  busy: boolean;
  disabled: boolean;
  error: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onFile?: (file: File) => void;
}) {
  const cannotSend = disabled || busy || !value.trim();
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <div>
      <div className="flex items-end gap-2">
        {onFile && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="sr-only"
              aria-label="Attach WhatsApp file"
              accept="image/jpeg,image/png,video/mp4,video/3gpp,audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg,text/plain,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              disabled={disabled || busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onFile(file);
                event.target.value = '';
              }}
            />
            <button
              type="button"
              aria-label="Attach photo, video, audio, or document"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || busy}
              className="rounded-lg border border-slate-300 p-2.5 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              +
            </button>
          </>
        )}
        <textarea
          aria-label="WhatsApp reply"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (shouldSendWhatsAppKey(event.key, event.shiftKey) && !cannotSend) {
              event.preventDefault();
              onSend();
            }
          }}
          rows={2}
          placeholder="Type a message"
          disabled={busy}
          className="min-h-10 flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-100"
        />
        <button
          type="button"
          onClick={onSend}
          disabled={cannotSend}
          className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50 active:scale-[0.98]"
        >
          {busy ? 'Sending...' : 'Send'}
        </button>
      </div>
      {error && <p role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

const CONSULTATION_STAGE_LABEL: Record<ConsultationStage, string> = {
  requested: 'Appointment requested — confirmation pending',
  booked: 'Consultation booked',
  confirmed: 'Appointment confirmed',
  attended: 'Consultation attended',
  missed: 'Consultation missed',
  cancelled: 'Consultation cancelled',
  rescheduled: 'Rescheduled',
  treatment_qualified: 'Treatment qualified',
  treatment_started: 'Treatment started / Cycle started',
};

function WonReasonModal({
  open,
  busy,
  consultationDate,
  consultationTime,
  consultationMode,
  consultationLocation,
  confirmationSource,
  confirmationReference,
  consultationError,
  onConsultationDateChange,
  onConsultationTimeChange,
  onConsultationModeChange,
  onConsultationLocationChange,
  onConfirmationSourceChange,
  onConfirmationReferenceChange,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  consultationDate: string;
  consultationTime: string;
  consultationMode: '' | 'Clinic' | 'Phone' | 'Video';
  consultationLocation: string;
  confirmationSource: '' | 'Practo' | 'Clinic register' | 'CRM manual';
  confirmationReference: string;
  consultationError: string;
  onConsultationDateChange: (value: string) => void;
  onConsultationTimeChange: (value: string) => void;
  onConsultationModeChange: (value: '' | 'Clinic' | 'Phone' | 'Video') => void;
  onConsultationLocationChange: (value: string) => void;
  onConfirmationSourceChange: (value: '' | 'Practo' | 'Clinic register' | 'CRM manual') => void;
  onConfirmationReferenceChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Confirm appointment</h3>
            <p className="mt-1 text-xs text-slate-500">Only confirm after checking Practo, the clinic register, or the authorised manual record.</p>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-sm text-slate-500 active:text-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>

        <div className="mt-3 space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold text-slate-600">
                Consultation date
                <input type="date" value={consultationDate} onChange={(e) => onConsultationDateChange(e.target.value)} disabled={busy} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="text-xs font-semibold text-slate-600">
                Consultation time
                <input type="time" value={consultationTime} onChange={(e) => onConsultationTimeChange(e.target.value)} disabled={busy} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
              </label>
            </div>
            <label className="text-xs font-semibold text-slate-600">
              Consultation mode
              <select value={consultationMode} onChange={(e) => onConsultationModeChange(e.target.value as '' | 'Clinic' | 'Phone' | 'Video')} disabled={busy} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
                <option value="">Select mode</option>
                <option value="Clinic">Clinic</option>
                <option value="Phone">Phone</option>
                <option value="Video">Video</option>
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Consultation location/details{consultationMode === 'Clinic' ? ' (required)' : ' (optional)'}
              <input value={consultationLocation} onChange={(e) => onConsultationLocationChange(e.target.value)} disabled={busy} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Confirmation source
              <select value={confirmationSource} onChange={(e) => onConfirmationSourceChange(e.target.value as '' | 'Practo' | 'Clinic register' | 'CRM manual')} disabled={busy} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
                <option value="">Select source</option>
                <option value="Practo">Practo</option>
                <option value="Clinic register">Clinic register</option>
                <option value="CRM manual">CRM manual</option>
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Confirmation reference
              <input value={confirmationReference} onChange={(e) => onConfirmationReferenceChange(e.target.value)} disabled={busy} placeholder="Practo appointment ID or register reference" className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
            </label>
            {consultationError && <p className="text-xs text-red-700">{consultationError}</p>}
          </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Confirm appointment'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReasonPickerModal({
  open,
  busy,
  selectedCode,
  customReason,
  title,
  confirmLabel,
  confirmTone,
  options,
  alwaysShowCustomReason = false,
  onSelect,
  onCustomReasonChange,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  selectedCode: string;
  customReason: string;
  title: string;
  confirmLabel: string;
  confirmTone: string;
  options: ReasonOption[];
  alwaysShowCustomReason?: boolean;
  onSelect: (code: string) => void;
  onCustomReasonChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  const canConfirm =
    !!selectedCode && (selectedCode !== 'other_reason' || customReason.trim().length > 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        </div>

        <div className="max-h-[55vh] overflow-y-auto px-5 py-4">
          <div className="space-y-2">
            {options.map((reason) => {
              const active = reason.code === selectedCode;
              return (
                <div key={reason.code} className="space-y-2">
                  <label
                    className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition ${
                      active
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    } ${busy ? 'opacity-60' : ''}`}
                  >
                    <input
                      type="radio"
                      checked={active}
                      onChange={() => onSelect(reason.code)}
                      disabled={busy}
                      className="h-5 w-5 border-slate-400 text-slate-900 focus:ring-slate-900"
                    />
                    <span className="flex-1 text-sm text-slate-800">{reason.label}</span>
                  </label>
                  {reason.code === 'other_reason' && active && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Add reason
                      </label>
                      <textarea
                        value={customReason}
                        onChange={(e) => onCustomReasonChange(e.target.value)}
                        disabled={busy}
                        rows={3}
                        placeholder="Type the reason here"
                        className="mt-2 w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none disabled:bg-slate-100"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || !canConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50 ${confirmTone}`}
          >
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function QualifiedReasonModal({
  open,
  busy,
  selectedCategory,
  selectedCode,
  title,
  confirmLabel,
  confirmTone,
  onSelectCategory,
  onSelectReason,
  onCancel,
  onConfirm,
  qualificationOutcome,
  qualificationOutcomeReason,
  nextActionDate,
  nextActionTime,
  consultationDate,
  consultationTime,
  consultationMode,
  consultationLocation,
  qualifiedCenter,
  availableSlots,
  availabilityLoading,
  availabilityError,
  onRetryAvailability,
  consultationError,
  onQualificationOutcome,
  onQualificationOutcomeReason,
  onNextActionDate,
  onNextActionTime,
  onConsultationDate,
  onConsultationTime,
  onConsultationMode,
  onConsultationLocation,
  onQualifiedCenter,
}: {
  open: boolean;
  busy: boolean;
  selectedCategory: QualifiedReasonCategory | '';
  selectedCode: string;
  title: string;
  confirmLabel: string;
  confirmTone: string;
  onSelectCategory: (value: QualifiedReasonCategory | '') => void;
  onSelectReason: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  qualificationOutcome: QualificationOutcome | '';
  qualificationOutcomeReason: string;
  nextActionDate: string;
  nextActionTime: string;
  consultationDate: string;
  consultationTime: string;
  consultationMode: '' | 'Clinic' | 'Phone' | 'Video';
  consultationLocation: string;
  qualifiedCenter: NonNullable<Telecaller['center']> | '';
  availableSlots: AppointmentSlot[];
  availabilityLoading: boolean;
  availabilityError: string;
  onRetryAvailability: () => void;
  consultationError: string;
  onQualificationOutcome: (value: QualificationOutcome | '') => void;
  onQualificationOutcomeReason: (value: string) => void;
  onNextActionDate: (value: string) => void;
  onNextActionTime: (value: string) => void;
  onConsultationDate: (value: string) => void;
  onConsultationTime: (value: string) => void;
  onConsultationMode: (value: '' | 'Clinic' | 'Phone' | 'Video') => void;
  onConsultationLocation: (value: string) => void;
  onQualifiedCenter: (value: NonNullable<Telecaller['center']> | '') => void;
}) {
  if (!open) return null;
  const reasonOptions =
    selectedCategory === 'infertility_issue'
      ? QUALIFIED_REASON_OPTIONS_BY_CATEGORY.infertility_issue
      : [];
  const canConfirm =
    (selectedCategory === 'gynic_issues' ||
      (selectedCategory === 'infertility_issue' && !!selectedCode)) &&
    (qualificationOutcome === 'booked'
      ? !!qualifiedCenter &&
        !!consultationDate &&
        !!consultationTime &&
        !!consultationMode &&
        !!consultationLocation.trim()
      : qualificationOutcome === 'positive'
        ? !!nextActionDate && !!nextActionTime
      : qualificationOutcome === 'pending'
        ? qualificationOutcomeReason.trim().length >= 3 &&
          !!nextActionDate &&
          !!nextActionTime
        : qualificationOutcome === 'declined'
          ? qualificationOutcomeReason.trim().length >= 3
          : false);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Qualification outcome
            </label>
            <select
              value={qualificationOutcome}
              onChange={(event) => onQualificationOutcome(
                event.target.value as QualificationOutcome | ''
              )}
              disabled={busy}
              className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Select outcome</option>
              <option value="positive">Positive — interested, will decide (follow up)</option>
              <option value="booked">Booked — appointment requested</option>
              <option value="pending">Undecided — needs information or time</option>
              <option value="declined">Not interested / declined</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Reason group
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                onSelectCategory((e.target.value as QualifiedReasonCategory) || '');
                onSelectReason('');
              }}
              disabled={busy}
              className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Select reason group</option>
              {QUALIFIED_REASON_CATEGORIES.map((category) => (
                <option key={category.code} value={category.code}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          {selectedCategory === 'infertility_issue' && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Infertility reason
              </label>
              <select
                value={selectedCode}
                onChange={(e) => onSelectReason(e.target.value)}
                disabled={busy}
                className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Select reason</option>
                {reasonOptions.map((reason) => (
                  <option key={reason.code} value={reason.code}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedCategory === 'gynic_issues' && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
              No extra reason is needed for Gynic Issues.
            </div>
          )}

          {qualificationOutcome === 'booked' ? (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Centre (hands over to the centre counsellor)</label>
              <select value={qualifiedCenter} onChange={(event) => onQualifiedCenter(event.target.value as NonNullable<Telecaller['center']> | '')} disabled={busy} className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
                <option value="">Select center</option>
                <option value="BBSR">BBSR</option>
                <option value="BAM">BAM</option>
                <option value="Angule">Angule</option>
              </select>
            </div>
          ) : qualificationOutcome === 'positive' ? (
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800">Positive stays with you, counts as qualified, and comes back in “Waiting for you” on the follow-up date.</p>
          ) : null}

          {qualificationOutcome === 'booked' && (
            <div className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="text-xs font-semibold text-slate-600">
                  Consultation date
                  <input type="date" value={consultationDate} onChange={(event) => onConsultationDate(event.target.value)} disabled={busy} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Consultation time
                  {consultationMode === 'Clinic' ? (
                    <select value={consultationTime} onChange={(event) => onConsultationTime(event.target.value)} disabled={busy || availabilityLoading || !!availabilityError || !availableSlots.length} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
                      <option value="">{availabilityLoading ? 'Checking available times…' : 'Select available time'}</option>
                      {availableSlots.map((slot) => <option key={`${slot.start_time}-${slot.end_time ?? ''}`} value={slot.start_time}>{slot.start_time}{slot.end_time ? `–${slot.end_time}` : ''}</option>)}
                    </select>
                  ) : (
                    <input type="time" value={consultationTime} onChange={(event) => onConsultationTime(event.target.value)} disabled={busy} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
                  )}
                </label>
              </div>
              <label className="text-xs font-semibold text-slate-600">
                Consultation mode
                <select value={consultationMode} onChange={(event) => onConsultationMode(event.target.value as '' | 'Clinic' | 'Phone' | 'Video')} disabled={busy} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
                  <option value="">Select mode</option>
                  <option value="Clinic">Clinic</option>
                  <option value="Phone">Phone</option>
                  <option value="Video">Video</option>
                </select>
              </label>
              <label className="text-xs font-semibold text-slate-600">
                Centre or counselling desk
                <input value={consultationLocation} onChange={(event) => onConsultationLocation(event.target.value)} disabled={busy} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
              </label>
              {availabilityError && <p className="text-xs text-red-700">{availabilityError} <button type="button" onClick={onRetryAvailability} className="font-semibold underline">Retry</button></p>}
              {consultationMode === 'Clinic' && !availabilityLoading && !availabilityError && qualifiedCenter && consultationDate && !availableSlots.length && <p className="text-xs text-slate-600">No appointment slots available for this date.</p>}
              {consultationError && <p className="text-xs text-red-700">{consultationError}</p>}
            </div>
          )}

          {(qualificationOutcome === 'pending' || qualificationOutcome === 'declined' || qualificationOutcome === 'positive') && (
            <label className="text-xs font-semibold text-slate-600">
              {qualificationOutcome === 'positive' ? 'Note (optional)' : qualificationOutcome === 'pending' ? 'What do they need before deciding?' : 'Why are they not interested?'}
              <input
                value={qualificationOutcomeReason}
                onChange={(event) => onQualificationOutcomeReason(event.target.value)}
                disabled={busy}
                placeholder="Record the patient’s stated reason"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              />
            </label>
          )}

          {(qualificationOutcome === 'pending' || qualificationOutcome === 'positive') && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold text-slate-600">
                Follow-up date
                <input type="date" value={nextActionDate} onChange={(event) => onNextActionDate(event.target.value)} disabled={busy} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="text-xs font-semibold text-slate-600">
                Follow-up time
                <input type="time" value={nextActionTime} onChange={(event) => onNextActionTime(event.target.value)} disabled={busy} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
              </label>
            </div>
          )}

        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || !canConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50 ${confirmTone}`}
          >
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const CALL_RESULT_STYLE: Record<CallResultRecord['kind'], string> = {
  in_progress: 'border-blue-200 bg-blue-50 text-blue-900',
  connected: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  patient: 'border-amber-200 bg-amber-50 text-amber-900',
  ended_early: 'border-slate-200 bg-slate-50 text-slate-800',
  technical: 'border-rose-200 bg-rose-50 text-rose-900',
  unconfirmed: 'border-violet-200 bg-violet-50 text-violet-900',
};

const CALL_RESULT_SOURCE: Record<CallResultRecord['kind'], string> = {
  in_progress: 'Call in progress',
  connected: 'Patient answered',
  patient: 'Patient side',
  ended_early: 'Ended early',
  technical: 'Technical — not the patient',
  unconfirmed: 'Not confirmed — not the patient',
};

function CallResultNotice({ result }: { result: CallResultRecord }) {
  const seconds = result.duration_seconds ?? 0;
  const started = result.started_at ? new Date(result.started_at) : null;
  return (
    <div className={`mt-3 rounded-lg border px-3 py-2 text-sm ${CALL_RESULT_STYLE[result.kind]}`} role="status" aria-live="polite">
      <div className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wide opacity-80">
        <span>Last call · {CALL_RESULT_SOURCE[result.kind]}</span>
        {started && !Number.isNaN(started.getTime()) && (
          <span className="font-normal normal-case">{started.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        )}
      </div>
      <div className="mt-1 font-semibold">
        {result.title}
        {result.kind === 'connected' && ` · ${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`}
      </div>
      <div className="mt-0.5 text-xs leading-5">{result.detail}</div>
    </div>
  );
}

function PhonePromptModal({
  open,
  busy,
  value,
  onChange,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  value: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  const canSave = value.trim().length >= 8;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:p-5">
        <div className="mb-3">
          <h3 className="text-base font-semibold text-slate-900">Add your phone number</h3>
          <p className="mt-1 text-sm text-slate-500">
            We use this number to place the Vobiz call and auto-fill the Call section.
          </p>
        </div>

        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Agent phone
        </label>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={busy}
          placeholder="Your phone (E.164, +9198…)"
          inputMode="tel"
          className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none disabled:bg-slate-100"
        />

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || !canSave}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save number'}
          </button>
        </div>
      </div>
    </div>
  );
}

const STAGE_LABEL: Record<string, string> = {
  new_inquiry: 'New enquiry', exploring: 'Exploring', interested: 'Interested', ready_to_book: 'Ready to book', booked: 'Booked',
  follow_up_needed: 'Needs follow-up', not_interested: 'Not interested', wrong_number_or_spam: 'Wrong number / spam',
};
const HINT_LABEL: Record<string, string> = { likely_positive: 'Positive', booked: 'Booked', declined: 'Declined', undecided: 'Undecided' };

export default function LeadDetail({
  leadId,
  me,
  userRole,
  onClose,
  onChanged,
  onWhatsAppRead,
  onNext,
  inline = false,
}: {
  leadId: string;
  me: string;
  userRole: string;
  onClose: () => void;
  onChanged: () => void;
  onWhatsAppRead?: () => void;
  /** Open the next lead from "Waiting for you" (Save & next on the result card). */
  onNext?: () => void;
  /** When true, renders as an inline right-pane (desktop split-view) instead of a bottom drawer. */
  inline?: boolean;
}) {
  const [lead, setLead] = useState<Lead | null>(null);
  const [events, setEvents] = useState<LeadEvent[]>([]);
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [outcomes, setOutcomes] = useState<LeadOutcome[]>([]);
  const [followups, setFollowups] = useState<Followup[]>([]);
  const [telecallers, setTelecallers] = useState<Telecaller[]>([]);
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [selectedTpl, setSelectedTpl] = useState<string>('');
  const [tplParams, setTplParams] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wonReasonOpen, setWonReasonOpen] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [wonReasonCode, setWonReasonCode] = useState('');
  const [wonReasonText, setWonReasonText] = useState('');
  const [dispositionReasonOpen, setDispositionReasonOpen] = useState(false);
  const [dispositionReasonStatus, setDispositionReasonStatus] = useState<LeadStatus | ''>('');
  const [dispositionReasonCode, setDispositionReasonCode] = useState('');
  const [dispositionReasonText, setDispositionReasonText] = useState('');
  const [qualifiedReasonOpen, setQualifiedReasonOpen] = useState(false);
  const [qualifiedReasonCategory, setQualifiedReasonCategory] = useState<QualifiedReasonCategory | ''>('');
  const [qualifiedReasonCode, setQualifiedReasonCode] = useState('');
  const [qualificationOutcomeRecord, setQualificationOutcomeRecord] =
    useState<QualificationOutcomeRecord | null>(null);
  const [qualificationOutcomeChoice, setQualificationOutcomeChoice] =
    useState<QualificationOutcome | ''>('');
  const [qualificationOutcomeReason, setQualificationOutcomeReason] = useState('');
  const [qualificationNextActionDate, setQualificationNextActionDate] = useState('');
  const [qualificationNextActionTime, setQualificationNextActionTime] = useState('');
  const [scheduleConsultation, setScheduleConsultation] = useState(false);
  const [consultationDate, setConsultationDate] = useState('');
  const [consultationTime, setConsultationTime] = useState('');
  const [consultationMode, setConsultationMode] = useState<'' | 'Clinic' | 'Phone' | 'Video'>('');
  const [consultationLocation, setConsultationLocation] = useState('');
  const [confirmationSource, setConfirmationSource] = useState<'' | 'Practo' | 'Clinic register' | 'CRM manual'>('');
  const [confirmationReference, setConfirmationReference] = useState('');
  const [consultationError, setConsultationError] = useState('');
  const [qualifiedCenter, setQualifiedCenter] = useState<NonNullable<Telecaller['center']> | ''>('');
  const [availableSlots, setAvailableSlots] = useState<AppointmentSlot[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [availabilityRefresh, setAvailabilityRefresh] = useState(0);
  const [consultationOutcome, setConsultationOutcome] = useState<ConsultationStage | ''>('');
  const [rescheduledDate, setRescheduledDate] = useState('');
  const [rescheduledTime, setRescheduledTime] = useState('');
  const [busyConsultation, setBusyConsultation] = useState(false);
  const [phonePromptOpen, setPhonePromptOpen] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [busyName, setBusyName] = useState(false);
  const [busyCall, setBusyCall] = useState(false);
  const [callResult, setCallResult] = useState<CallResultRecord | null>(null);
  // Bumped to cancel an in-flight watch when the lead changes or a new call starts.
  const callWatch = useRef(0);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [busyWa, setBusyWa] = useState(false);
  const [waText, setWaText] = useState('');
  const [waSendError, setWaSendError] = useState('');
  const [whatsappMessages, setWhatsappMessages] = useState<WhatsAppMessageRecord[]>([]);
  const [whatsappProvider, setWhatsappProvider] = useState('');
  const [pendingDisposition, setPendingDisposition] = useState<InteractionDisposition | null>(null);
  const [conversationAssist, setConversationAssist] = useState<ConversationAssist | null>(null);
  const [assistBusy, setAssistBusy] = useState(false);
  const whatsappRefreshInFlight = useRef<Promise<void> | null>(null);
  const [showWhatsAppInbox, setShowWhatsAppInbox] = useState(false);
  const [phone, setPhone] = useState(getTelecallerPhone());
  const [customerName, setCustomerName] = useState('');
  const [noteText, setNoteText] = useState('');
  const [busyNote, setBusyNote] = useState(false);
  const [showMetaTechnical, setShowMetaTechnical] = useState(false);
  const [attribution, setAttribution] = useState<LeadAttribution | null>(null);
  const [payment, setPayment] = useState<{
    amount_minor: number;
    currency: string;
    provider_payment_id: string | null;
    verified_at: string | null;
    location: string | null;
  } | null>(null);
  const [reportedSource, setReportedSource] = useState<LeadReportedSource | null>(null);
  const [reportedSourceCategory, setReportedSourceCategory] = useState<LeadReportedSource['category'] | ''>('');
  const [reportedSourceDetail, setReportedSourceDetail] = useState('');
  const [busyReportedSource, setBusyReportedSource] = useState(false);
  
  // Follow-up form state
  const [showFollowupForm, setShowFollowupForm] = useState(false);
  const [followupDate, setFollowupDate] = useState('');
  const [followupTime, setFollowupTime] = useState('');
  const [followupNotes, setFollowupNotes] = useState('');
  const [busyFollowup, setBusyFollowup] = useState(false);
  const quickFollowupDays = [1, 2, 3, 5, 7];
  const neodoveDetails = useMemo(
    () => (lead?.source === 'neodove' ? formatNeoDoveAnswers(lead.raw_answers) : []),
    [lead?.source, lead?.raw_answers]
  );
  const agentNotes = useMemo(() => extractAgentNotes(events), [events]);

  useEffect(() => {
    let active = true;
    setAvailableSlots([]);
    setAvailabilityError('');
    if (!qualifiedReasonOpen || qualificationOutcomeChoice !== 'booked' || consultationMode !== 'Clinic' || !qualifiedCenter || !consultationDate) {
      setAvailabilityLoading(false);
      return () => { active = false; };
    }
    setAvailabilityLoading(true);
    fetchAppointmentAvailability(qualifiedCenter, consultationDate)
      .then((result) => { if (active) setAvailableSlots(result.slots); })
      .catch((error) => { if (active) setAvailabilityError(error instanceof Error ? error.message : 'Could not load available times.'); })
      .finally(() => { if (active) setAvailabilityLoading(false); });
    return () => { active = false; };
  }, [qualifiedReasonOpen, qualificationOutcomeChoice, consultationMode, qualifiedCenter, consultationDate, availabilityRefresh]);

  async function load() {
    try {
      const l = await fetchLead(leadId);
      const [tc, tpl, fu, lastCall] = await Promise.all([
        fetchTelecallers().catch(() => ({ telecallers: [] as Telecaller[] })),
        fetchWaTemplates().catch(() => ({ templates: [] as WaTemplate[] })),
        fetchLeadFollowups(leadId).catch(() => ({ followups: [] as Followup[] })),
        fetchCallResult(leadId).catch(() => ({ call: null })),
      ]);
      setCallResult(lastCall.call);
      setLead(l.lead);
      setCustomerName(l.lead.name || '');
      setEvents(l.events);
      setCalls(l.calls);
      setOutcomes(l.outcomes ?? []);
      setQualificationOutcomeRecord(l.qualification_outcome ?? null);
      setAttribution(l.attribution ?? null);
      setPayment(l.payment ?? null);
      setReportedSource(l.reported_source ?? null);
      setReportedSourceCategory(l.reported_source?.category ?? '');
      setReportedSourceDetail(l.reported_source?.detail ?? '');
      setTelecallers(tc.telecallers);
      setTemplates(tpl.templates);
      setFollowups(fu.followups);
      const pending = await fetchPendingDispositions({ leadId });
      setPendingDisposition(pending.items[0] || null);
      setConsultationOutcome(l.lead.consultation_status as ConsultationStage || (l.lead.consultation_date ? 'booked' : ''));
      return true;
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'load failed');
      return false;
    }
  }

  useEffect(() => {
    setLead(null);
    setCallResult(null);
    callWatch.current++;
    void (async () => {
      try {
        if (!await load()) return;
        await markWhatsAppRead(leadId);
        onWhatsAppRead?.();
      } catch {
        // Lead is unavailable; do not start dependent read-state requests.
      }
    })();
    return () => { callWatch.current++; };
  }, [leadId, onWhatsAppRead]);

  useEffect(() => {
    setShowMetaTechnical(false);
    setActionNotice(null);
    setWhatsappMessages([]);
    setWhatsappProvider('');
    setPendingDisposition(null);
    setConversationAssist(null);
    setWaSendError('');
    setShowWhatsAppInbox(false);
    void fetchPendingDispositions({ leadId }).then((result) => setPendingDisposition(result.items[0] || null)).catch(() => undefined);
  }, [leadId]);

  async function refreshWhatsAppMessages() {
    if (!leadId) return;
    if (whatsappRefreshInFlight.current) return whatsappRefreshInFlight.current;

    const request = (async () => {
      try {
        const latest = await fetchWhatsAppMessages(leadId);
        setWhatsappProvider(latest.provider);
        setWhatsappMessages((current) => reconcileWhatsAppHistory(current, latest));
      } catch {
        // Provider may not be Wati or endpoint unavailable — legacy presentation still works.
      } finally {
        whatsappRefreshInFlight.current = null;
      }
    })();
    whatsappRefreshInFlight.current = request;
    return request;
  }

  async function suggestWhatsAppReply() {
    if (!leadId || assistBusy) return;
    setAssistBusy(true);
    setWaSendError('');
    try {
      const result = await fetchConversationReplySuggestion(leadId);
      setConversationAssist(result.suggestion);
    } catch (error) {
      setWaSendError(error instanceof Error ? error.message : 'Reply suggestion is unavailable');
    } finally { setAssistBusy(false); }
  }

  // The qualifier runs by itself when a chat opens or a new patient message arrives (cached per message on the server).
  const latestInboundId = [...whatsappMessages].reverse().find((m) => m.direction === 'inbound')?.id ?? '';
  const qualifiedFor = useRef('');
  useEffect(() => {
    if (!leadId || !latestInboundId || qualifiedFor.current === `${leadId}:${latestInboundId}`) return;
    qualifiedFor.current = `${leadId}:${latestInboundId}`;
    fetchConversationReplySuggestion(leadId).then((result) => setConversationAssist(result.suggestion)).catch(() => undefined);
  }, [leadId, latestInboundId]);

  useWhatsAppWebSocket(
    lead?.id === leadId ? leadId : '',
    (event: WhatsAppRealtimeEvent) => {
      setWhatsappMessages((current) => applyWhatsAppRealtimeEvent(current, event));
    },
    () => { void refreshWhatsAppMessages(); },
  );

  // Fetch live WhatsApp messages from Wati when the lead loads
  useEffect(() => {
    if (!leadId || !lead?.phone) return;
    void refreshWhatsAppMessages();
  }, [leadId, lead?.phone]);

  useEffect(() => {
    const savedPhone = getTelecallerPhone().trim();
    setPhone(savedPhone);
    if (!savedPhone) {
      setPhoneDraft('');
      setPhonePromptOpen(true);
    } else {
      setPhonePromptOpen(false);
    }
  }, [leadId]);

  function openAppointmentConfirmation() {
    if (!lead || blockIfLocked()) return;
      if (!['admin', 'manager', 'super_admin'].includes(userRole)) {
        setActionNotice('Only a manager or administrator can confirm an appointment.');
        return;
      }
      if (!['requested', 'booked'].includes(String(lead.consultation_status ?? ''))) {
        setActionNotice('Create an appointment request first by qualifying the lead as booked.');
        return;
      }
      if (qualificationOutcomeRecord?.outcome !== 'booked') {
        setActionNotice('The lead must have a booked qualification outcome before confirmation.');
        return;
      }
      setWonReasonOpen(true);
      setWonReasonCode('booked_consult');
      setWonReasonText('Appointment confirmed by authorised staff');
      setScheduleConsultation(true);
      setConsultationDate(lead.consultation_date ?? '');
      setConsultationTime(lead.consultation_time ?? '');
      setConsultationMode((lead.consultation_mode as '' | 'Clinic' | 'Phone' | 'Video') || '');
      setConsultationLocation(lead.consultation_location ?? '');
      setConfirmationSource('');
      setConfirmationReference('');
      setConsultationError('');
      return;
  }

  async function changeStatus(next: LeadStatus) {
    if (!lead || (next === lead.status && !['qualified', 'contacted', 'lost'].includes(next))) return;
    if (blockIfLocked()) return;
    if (next === 'won') {
      if (!['admin', 'manager', 'super_admin'].includes(userRole)) { setActionNotice('Only a manager or administrator can record registration.'); return; }
      setRegistrationOpen(true);
      return;
    }
    if (next === 'qualified') {
      setQualifiedReasonOpen(true);
      setQualifiedReasonCategory('');
      setQualifiedReasonCode('');
      setQualificationOutcomeChoice('');
      setQualificationOutcomeReason('');
      setQualificationNextActionDate('');
      setQualificationNextActionTime('');
      setScheduleConsultation(false);
      setConsultationDate('');
      setConsultationTime('');
      setConsultationMode('');
      setConsultationLocation('');
      setConfirmationSource('');
      setConfirmationReference('');
      setConsultationError('');
      return;
    }
    if (next === 'lost' || next === 'invalid_lead' || next === 'contacted') {
      setDispositionReasonOpen(true);
      setDispositionReasonStatus(next);
      setDispositionReasonCode('');
      setDispositionReasonText('');
      return;
    }
    setBusy(true);
    try {
      await updateStatus(lead.id, next);
      await load();
      onChanged();
    } catch (e) {
      alert(`Status change failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function confirmDisposition() {
    if (blockIfLocked()) return;
    if (!lead || !dispositionReasonStatus || !dispositionReasonCode) return;
    const reasonOptions = dispositionReasonStatus === 'contacted' ? CONTACTED_REASONS : dispositionReasonStatus === 'invalid_lead' ? REJECTED_REASONS : LOST_REASONS;
    const selected = reasonOptions.find((reason) => reason.code === dispositionReasonCode);
    const reasonText =
      dispositionReasonCode === 'other_reason' ? dispositionReasonText.trim() : selected?.label;
    if (dispositionReasonCode === 'other_reason' && !reasonText) return;
    setBusy(true);
    try {
      await updateStatus(lead.id, dispositionReasonStatus, {
        reason_code: dispositionReasonCode,
        reason_text: reasonText,
      });
      setDispositionReasonOpen(false);
      setDispositionReasonStatus('');
      setDispositionReasonCode('');
      setDispositionReasonText('');
      await load();
      onChanged();
    } catch (e) {
      alert(`Status change failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function confirmQualifiedReason() {
    if (blockIfLocked()) return;
    if (!lead || !qualificationOutcomeChoice) return;
    if (!qualifiedReasonCategory) return;
    if (qualificationOutcomeChoice === 'booked' && !qualifiedCenter) return;
    const reasonOptions =
      qualifiedReasonCategory === 'infertility_issue'
        ? QUALIFIED_REASON_OPTIONS_BY_CATEGORY.infertility_issue
        : QUALIFIED_REASON_OPTIONS_BY_CATEGORY.gynic_issues;
    const selected =
      qualifiedReasonCategory === 'gynic_issues'
        ? { code: 'gynic_issues', label: 'Gynic Issues' }
        : reasonOptions.find((reason) => reason.code === qualifiedReasonCode);
    const reasonCode = qualifiedReasonCategory === 'gynic_issues' ? 'gynic_issues' : selected?.code ?? '';
    const reasonText = selected?.label ?? '';
    if (qualifiedReasonCategory === 'infertility_issue' && !reasonCode) return;
    if (qualificationOutcomeChoice === 'booked') {
      const bookingAt = new Date(`${consultationDate}T${consultationTime}:00`);
      const error = !consultationDate || !consultationTime || !consultationMode ||
        !consultationLocation.trim()
        ? 'Date, time, mode, and centre/desk are required.'
        : Number.isNaN(bookingAt.getTime()) || (bookingAt <= new Date() && userRole !== 'super_admin')
          ? 'Consultation booking cannot be in the past.'
          : '';
      if (error) {
        setConsultationError(error);
        return;
      }
    }
    setBusy(true);
    try {
      await updateStatus(lead.id, 'qualified', {
        reason_code: reasonCode,
        reason_text: reasonText,
        reason_group: 'qualified_issue',
        reason_category: qualifiedReasonCategory,
        qualification_outcome: qualificationOutcomeChoice,
        center: qualificationOutcomeChoice === 'booked' && qualifiedCenter ? qualifiedCenter : undefined,
        qualification_outcome_reason: qualificationOutcomeReason.trim() || undefined,
        next_action_date:
          qualificationOutcomeChoice === 'pending' || qualificationOutcomeChoice === 'positive'
            ? qualificationNextActionDate
            : undefined,
        next_action_time:
          qualificationOutcomeChoice === 'pending' || qualificationOutcomeChoice === 'positive'
            ? qualificationNextActionTime
            : undefined,
        ...(qualificationOutcomeChoice === 'booked' ? {
          schedule_consultation: true,
          consultation_date: consultationDate,
          consultation_time: consultationTime,
          consultation_mode: consultationMode || undefined,
          consultation_location: consultationLocation.trim(),
        } : {}),
      });
      setQualifiedReasonOpen(false);
      setQualifiedReasonCategory('');
      setQualifiedReasonCode('');
      setQualificationOutcomeChoice('');
      setQualificationOutcomeReason('');
      setQualificationNextActionDate('');
      setQualificationNextActionTime('');
      await load();
      onChanged();
    } catch (e) {
      if (qualificationOutcomeChoice === 'booked' && consultationMode === 'Clinic') setAvailabilityRefresh((value) => value + 1);
      alert(`Status change failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function confirmWon() {
    if (blockIfLocked()) return;
    if (!lead) return;
    const bookingAt = new Date(`${consultationDate}T${consultationTime}:00`);
    const error = !consultationDate
      ? 'Consultation date is required.'
      : !consultationTime
        ? 'Consultation time is required.'
        : !consultationMode
          ? 'Consultation mode is required.'
          : Number.isNaN(bookingAt.getTime()) || (bookingAt <= new Date() && userRole !== 'super_admin')
            ? 'Consultation booking cannot be in the past.'
            : consultationMode === 'Clinic' && !consultationLocation.trim()
              ? 'Clinic consultation location is required.'
              : !confirmationSource
                ? 'Confirmation source is required.'
                : !confirmationReference.trim()
                  ? 'Confirmation reference is required.'
                  : '';
    if (error) {
      setConsultationError(error);
      return;
    }
    setBusy(true);
    try {
      await confirmConsultation(lead.id, {
        consultation_date: consultationDate,
        consultation_time: consultationTime,
        consultation_mode: consultationMode as 'Clinic' | 'Phone' | 'Video',
        consultation_location: consultationLocation.trim() || undefined,
        confirmation_source: confirmationSource as 'Practo' | 'Clinic register' | 'CRM manual',
        confirmation_reference: confirmationReference.trim(),
      });
      setWonReasonOpen(false);
      setWonReasonCode('');
      setWonReasonText('');
      setScheduleConsultation(false);
      setConsultationDate('');
      setConsultationTime('');
      setConsultationMode('');
      setConsultationLocation('');
      setConfirmationSource('');
      setConfirmationReference('');
      setConsultationError('');
      await load();
      onChanged();
    } catch (e) {
      alert(`Status change failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function updateConsultationStage() {
    if (!lead || !consultationOutcome || blockIfLocked()) return;
    if (consultationOutcome === 'treatment_started' && !window.confirm('Mark treatment started / cycle started for this lead?')) return;
    if (consultationOutcome === 'rescheduled' && (!rescheduledDate || !rescheduledTime)) {
      alert('New consultation date and time are required.');
      return;
    }
    setBusyConsultation(true);
    try {
      await updateConsultationOutcome(lead.id, consultationOutcome, consultationOutcome === 'rescheduled'
        ? { consultation_date: rescheduledDate, consultation_time: rescheduledTime }
        : undefined);
      setRescheduledDate('');
      setRescheduledTime('');
      await load();
      onChanged();
    } catch (e) {
      alert(`Consultation outcome update failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusyConsultation(false);
    }
  }

  async function changeTier(next: 1 | 2 | 3) {
    if (!lead || next === lead.tier_score) return;
    if (blockIfLocked()) return;
    setBusy(true);
    try {
      await updateTier(lead.id, next);
      await load();
      onChanged();
    } catch (e) {
      alert(`Tier change failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function markCareMilestone(milestone: CareMilestone) {
    if (!lead || milestone === 'lead_received') return;
    if (blockIfLocked()) return;

    const label = CARE_MILESTONE_LABEL[milestone];
    let valueAmount: number | null = null;
    if (milestone === 'revenue') {
      const raw = window.prompt('Revenue amount in INR (required).');
      if (raw === null) return;
      const parsed = Number(raw.replace(/,/g, ''));
      if (!Number.isFinite(parsed) || parsed <= 0) {
        alert('Please enter a positive revenue amount.');
        return;
      }
      valueAmount = parsed;
    } else if (!window.confirm(`Mark "${label}" for this lead?`)) {
      return;
    }

    setBusy(true);
    try {
      const res = await recordLeadMilestone(lead.id, milestone, { value_amount: valueAmount });
      setOutcomes(res.outcomes);
      await load();
      onChanged();
    } catch (e) {
      alert(`Care progress update failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function assign(email: string | null) {
    if (!lead) return;
    setBusy(true);
    try {
      if (isTelecaller && email === me && !lead.assigned_to) {
        await claimLead(lead.id);
      } else {
        await assignLead(lead.id, email);
      }
      await load();
      onChanged();
    } catch (e) {
      setActionNotice(friendlyAssignmentMessage(e));
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function saveCustomerName() {
    if (!lead) return;
    if (blockIfLocked()) return;
    const name = customerName.trim();
    if (!name) {
      alert('Customer name cannot be empty');
      return;
    }
    if (name === lead.name) return;

    setBusyName(true);
    try {
      await updateLeadName(lead.id, name);
      await load();
      onChanged();
    } catch (e) {
      alert(`Name update failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusyName(false);
    }
  }

  async function saveReportedSource() {
    if (!lead || !reportedSourceCategory || blockIfLocked()) return;
    if (reportedSourceCategory === 'other' && !reportedSourceDetail.trim()) {
      setActionNotice('Please add a short source detail for Other.');
      return;
    }
    setBusyReportedSource(true);
    try {
      const result = await updateReportedSource(lead.id, {
        category: reportedSourceCategory,
        detail: reportedSourceDetail.trim() || undefined,
      });
      setReportedSource(result.reported_source);
      setActionNotice('Patient-reported origin saved separately from system attribution.');
      await load();
      onChanged();
    } catch (error) {
      setActionNotice(`Could not save patient-reported origin: ${error instanceof Error ? error.message : 'unknown error'}`);
    } finally {
      setBusyReportedSource(false);
    }
  }

  // Poll until the worker reports a final result; the worker stops claiming "in progress" after two minutes.
  async function watchCallResult(watchedLeadId: string) {
    const token = ++callWatch.current;
    const deadline = Date.now() + 150_000;
    while (callWatch.current === token && Date.now() < deadline) {
      try {
        const { call: latest } = await fetchCallResult(watchedLeadId);
        if (callWatch.current !== token) return;
        setCallResult(latest);
        if (latest?.final) return;
      } catch {
        // Keep the last known result; the next poll may succeed.
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
  }

  async function call() {
    if (!lead) return;
    if (busyCall) return;
    if (blockIfLocked()) return;
    if (!phone) {
      setActionNotice('Add your phone number first. VoBiz will call you and then connect the patient.');
      return;
    }
    setBusyCall(true);
    try {
      await initCall(lead.id, phone);
      await load();
      onChanged();
      void watchCallResult(lead.id);
    } catch (e) {
      setActionNotice(friendlyCallMessage(e));
    } finally {
      setBusyCall(false);
    }
  }

  const currentTemplate = useMemo(
    () => templates.find((t) => t.name === selectedTpl),
    [templates, selectedTpl]
  );

  function pickTemplate(name: string) {
    setSelectedTpl(name);
    const tpl = templates.find((t) => t.name === name);
    if (!tpl) {
      setTplParams([]);
      return;
    }
    // Smart defaults for common param slots
    setTplParams(
      tpl.params.map((label) => {
        const l = label.toLowerCase();
        if (l.includes('patient') || l.includes('name')) return lead?.name ?? '';
        if (l.includes('clinic')) return 'Santaan Fertility';
        if (l.includes('telecaller')) return me.split('@')[0];
        return '';
      })
    );
  }

  async function sendTemplate() {
    if (blockIfLocked()) return;
    if (!lead || !selectedTpl) return;
    setBusyWa(true);
    try {
      const res = await sendWhatsapp(lead.id, {
        template_name: selectedTpl,
        params: tplParams,
      });
      setPendingDisposition(res.disposition || null);
      await load();
      onChanged();
      alert(`Sent via ${res.provider ?? 'API'}. Response: ${res.raw ?? 'ok'}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown';
      if (msg.includes('501')) {
        alert('WhatsApp provider not configured on worker.');
      } else {
        alert(`Send failed: ${msg}`);
      }
    } finally {
      setBusyWa(false);
    }
  }

  async function sendSessionMessage() {
    if (!lead || !String(lead.phone || '').trim() || !waText.trim() || busyWa || blockIfLocked()) return;
    const text = waText.trim();
    const clientTempId = crypto.randomUUID();
    const optimistic = createOptimisticWhatsAppMessage(text, clientTempId, new Date().toISOString());
    setWhatsappMessages((current) => mergeWhatsAppMessageRecords(current, optimistic));
    setWaText('');
    setWaSendError('');
    setBusyWa(true);
    try {
      const result = await sendWhatsapp(lead.id, { text, client_temp_id: clientTempId });
      setPendingDisposition(result.disposition || null);
      const saved = result.message ?? {
        ...optimistic,
        provider_message_id: result.provider_message_id ?? null,
        status: result.status === 'sent' ? 'sent' as const : 'submitted' as const,
      };
      setWhatsappMessages((current) => mergeWhatsAppMessageRecords(current, saved));
    } catch (error) {
      setWhatsappMessages((current) => markWhatsAppMessageFailed(current, clientTempId));
      setWaText((current) => current || text);
      setWaSendError(friendlyWhatsAppSendError(error));
    } finally {
      setBusyWa(false);
    }
  }

  async function sendFile(file: File) {
    if (!lead || busyWa || blockIfLocked()) return;
    const caption = waText.trim();
    setWaText('');
    setWaSendError('');
    setBusyWa(true);
    try {
      const result = await sendWhatsAppFile(lead.id, file, caption);
      setPendingDisposition(result.disposition || null);
      if (result.message) {
        setWhatsappMessages((current) => mergeWhatsAppMessageRecords(current, result.message!));
      }
    } catch (error) {
      setWaText((current) => current || caption);
      setWaSendError(friendlyWhatsAppSendError(error));
    } finally {
      setBusyWa(false);
    }
  }

  function savePhone(v: string) {
    setPhone(v);
    setTelecallerPhone(v);
  }

  function savePromptPhone() {
    const normalized = phoneDraft.trim();
    if (!normalized) return;
    savePhone(normalized);
    setPhonePromptOpen(false);
  }

  async function handleCreateFollowup() {
    if (blockIfLocked()) return;
    if (!contactPolicy.messageAllowed) { setActionNotice(contactPolicy.guidance); return; }
    if (!followupDate) {
      alert('Please select a date for the follow-up');
      return;
    }
    if (!lead) return;
    
    setBusyFollowup(true);
    try {
      await createFollowup(lead.id, {
        followup_date: followupDate,
        followup_time: followupTime || undefined,
        notes: followupNotes || undefined,
      });
      await load();
      onChanged();
      setShowFollowupForm(false);
      setFollowupDate('');
      setFollowupTime('');
      setFollowupNotes('');
    } catch (e) {
      alert(`Failed to create follow-up: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusyFollowup(false);
    }
  }

  async function scheduleQuickFollowup(daysAhead: number) {
    if (blockIfLocked()) return;
    if (!contactPolicy.messageAllowed) { setActionNotice(contactPolicy.guidance); return; }
    if (!lead) return;

    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + daysAhead);
    nextDate.setHours(10, 0, 0, 0);

    setBusyFollowup(true);
    try {
      await createFollowup(lead.id, {
        followup_date: formatLocalDateInput(nextDate),
        followup_time: '10:00',
      });
      await load();
      onChanged();
      setShowFollowupForm(false);
      setFollowupDate('');
      setFollowupTime('');
      setFollowupNotes('');
    } catch (e) {
      alert(`Failed to create follow-up: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusyFollowup(false);
    }
  }

  async function addNote() {
    if (!lead) return;
    if (blockIfLocked()) return;
    const note = noteText.trim();
    if (!note) return;

    setBusyNote(true);
    try {
      await addLeadNote(lead.id, note);
      setNoteText('');
      await load();
      onChanged();
    } catch (e) {
      alert(`Note failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusyNote(false);
    }
  }

  async function handleCompleteFollowup(followupId: string) {
    if (blockIfLocked()) return;
    if (!confirm('Mark this follow-up as completed?')) return;
    
    try {
      await updateFollowupStatus(followupId, 'completed');
      await load();
      onChanged();
    } catch (e) {
      alert(`Failed to update follow-up: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  const mine = lead?.assigned_to === me;
  const isTelecaller = userRole === 'telecaller';
  const latestCallStatus = calls.length
    ? String((calls.reduce((a, b) => (a.created_at >= b.created_at ? a : b)) as { call_status?: string }).call_status ?? '') || null
    : null;
  const isManager = userRole === 'manager';
  const lockedForLeadActions = !!lead && (isTelecaller || isManager) && !mine;
  const contactPolicy = leadContactPolicy(lead);
  const previousAssignee =
    lead?.last_assigned_to && lead.last_assigned_to !== lead.assigned_to
      ? lead.last_assigned_to.split('@')[0]
      : null;
  const blockedMessage = lead?.assigned_to
    ? isManager
      ? 'Assign this lead to yourself before making changes.'
      : 'This lead is assigned to another health advisor.'
    : 'Assign this lead to yourself before making changes.';

  function blockIfLocked() {
    if (!lockedForLeadActions) return false;
    setActionNotice(blockedMessage);
    return true;
  }

  const outcomeByMilestone = new Map(outcomes.map((outcome) => [outcome.milestone, outcome]));
  const consultationDataExists = Boolean(lead?.consultation_status || lead?.consultation_date || lead?.consultation_time);
  const currentConsultationStage =
    lead?.consultation_status && lead.consultation_status in CONSULTATION_STAGE_LABEL
      ? lead.consultation_status as ConsultationStage
      : lead?.consultation_date
        ? 'booked'
        : '';
  const attributionState = attribution?.campaign_id && attribution?.adset_id && attribution?.ad_id
    ? { label: 'Complete paid attribution', className: 'bg-emerald-100 text-emerald-800' }
    : attribution?.ad_id || attribution?.ctwa_clid
      ? { label: 'Partial paid attribution', className: 'bg-amber-100 text-amber-800' }
      : attribution?.provider_message_id
        ? { label: 'Provider identity only', className: 'bg-slate-100 text-slate-700' }
        : { label: 'No verified system attribution', className: 'bg-rose-100 text-rose-800' };

  const detailInner = (
    <>
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">{lead?.name ?? 'Loading…'}</h2>
          {lead?.is_blocked && <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700">Blocked</span>}
        </div>
        <button onClick={onClose} className="text-sm text-slate-500 active:text-slate-800">
          Close
        </button>
      </div>
      <div
        className={
          inline
            ? 'flex-1 space-y-4 overflow-y-auto px-4 py-4'
            : 'max-h-[80vh] space-y-4 overflow-y-auto px-4 py-4 pb-[env(safe-area-inset-bottom,16px)]'
        }
      >
          {lead && (['admin', 'manager', 'super_admin'].includes(userRole) || FEATURE_ACCESS.JEV_ASSIST_EMAILS.includes(me.trim().toLowerCase())) && (
            <JevStrip key={`jev-${lead.id}`} leadId={lead.id} canEdit={!lockedForLeadActions} />
          )}
          {lead && !lockedForLeadActions && !['won', 'invalid_lead'].includes(lead.status) && (
            <ResultCard
              key={lead.id}
              leadId={lead.id}
              lastCallStatus={latestCallStatus}
              hint={conversationAssist?.qualification_hint ?? null}
              onSaved={(openNext) => { void load(); onChanged(); if (openNext) onNext?.(); }}
            />
          )}
          {lockedForLeadActions && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {blockedMessage}
            </div>
          )}
          {err && <p className="rounded-lg bg-red-50 p-2 text-xs text-red-700">{err}</p>}
          {!lead && !err && <p className="text-center text-sm text-slate-500">Loading…</p>}

          {lead && (
            <>
              <section className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1 pr-3">
                    <div className="text-xs text-slate-500">Customer name</div>
                    <div className="mt-1 flex gap-2">
                      <input
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        onBlur={saveCustomerName}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        disabled={busyName || lockedForLeadActions}
                        placeholder="Add customer name"
                        className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      />
                      <button
                        onClick={saveCustomerName}
                        disabled={busyName || customerName.trim() === lead.name || lockedForLeadActions}
                        className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <span
                    className={`flex-none rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLOR[lead.status]}`}
                  >
                    {STATUS_LABEL[lead.status]}
                  </span>
                </div>
                <div className="mt-3 flex items-start justify-between">
                  <div>
                    <div className="text-xs text-slate-500">Phone</div>
                    <div className="text-sm font-medium text-slate-900">{lead.phone}</div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-slate-500">Created</div>
                <div className="text-xs text-slate-700">{lead.created_at}</div>
                {(isMapLead(lead) || payment) && (
                  <section className="mt-3 rounded-lg border border-teal-200 bg-teal-50 p-3 text-xs">
                    <div className="mb-2 font-semibold text-teal-950">Map context</div>
                    {isMapConsultationLead(lead) && (() => {
                      const details = mapLeadDetails(lead);
                      return (
                        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                          <div><div className="text-teal-700">Request</div><div className="font-semibold text-teal-950">Consultation</div></div>
                          <div><div className="text-teal-700">Centre</div><div className="font-semibold text-teal-950">{details.centre || '—'}</div></div>
                          <div><div className="text-teal-700">Preferred date</div><div className="font-semibold text-teal-950">{details.preferredWindow || '—'}</div></div>
                          <div><div className="text-teal-700">Source</div><div className="font-semibold text-teal-950">map.santaan.in</div></div>
                          <div className="col-span-2"><div className="text-teal-700">Booking status</div><div className="font-semibold text-teal-950">{lead.consultation_confirmation_status === 'confirmed' ? 'Appointment Confirmed' : 'Requested / Pending confirmation'}</div></div>
                        </div>
                      );
                    })()}
                    {payment && (
                      <div className={isMapConsultationLead(lead) ? 'mt-3 border-t border-teal-200 pt-3' : ''}>
                        <div className="font-semibold text-emerald-900">Payment: ₹{(payment.amount_minor / 100).toLocaleString('en-IN')} Paid</div>
                        <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-2">
                          <div><div className="text-teal-700">Centre</div><div className="font-semibold text-teal-950">{payment.location || lead.clinic_tag || '—'}</div></div>
                          <div><div className="text-teal-700">Reference</div><div className="font-semibold text-teal-950">{payment.provider_payment_id || '—'}</div></div>
                          <div className="col-span-2"><div className="text-teal-700">Verified</div><div className="font-semibold text-teal-950">{payment.verified_at || '—'}</div></div>
                        </div>
                      </div>
                    )}
                  </section>
                )}
                {qualificationOutcomeRecord && (
                  <div className="mt-3 rounded-lg border border-purple-200 bg-purple-50 p-2 text-xs">
                    <div className="font-semibold text-purple-900">
                      Qualification handoff: {qualificationOutcomeRecord.outcome}
                    </div>
                    {qualificationOutcomeRecord.reason_text && (
                      <div className="mt-1 text-purple-800">
                        {qualificationOutcomeRecord.reason_text}
                      </div>
                    )}
                    {qualificationOutcomeRecord.next_action_at && (
                      <div className="mt-1 text-purple-800">
                        Next action: {new Date(qualificationOutcomeRecord.next_action_at).toLocaleString('en-IN')}
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-3 border-t border-slate-200 pt-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Consultation</div>
                    {consultationDataExists && <span className="text-[10px] text-slate-400">{lead.consultation_updated_at || lead.consultation_booked_at || '—'}</span>}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                    <div><div className="text-slate-500">Scheduled</div><div className="font-medium text-slate-800">{lead.consultation_date && lead.consultation_time ? `${lead.consultation_date} ${lead.consultation_time}` : '—'}</div></div>
                    <div><div className="text-slate-500">Mode</div><div className="font-medium text-slate-800">{lead.consultation_mode || '—'}</div></div>
                    <div><div className="text-slate-500">Location/details</div><div className="font-medium text-slate-800">{lead.consultation_location || '—'}</div></div>
                    <div><div className="text-slate-500">Current stage</div><div className="font-medium text-slate-800">{currentConsultationStage ? CONSULTATION_STAGE_LABEL[currentConsultationStage] : '—'}</div></div>
                    <div><div className="text-slate-500">Confirmation source</div><div className="font-medium text-slate-800">{lead.consultation_confirmation_source || 'Pending manual check'}</div></div>
                    <div><div className="text-slate-500">Confirmation reference</div><div className="font-medium text-slate-800">{lead.consultation_confirmation_reference || '—'}</div></div>
                  </div>
                  {['requested', 'booked'].includes(String(lead.consultation_status)) && ['admin', 'manager', 'super_admin'].includes(userRole) && <button onClick={openAppointmentConfirmation} disabled={busy || lockedForLeadActions} className="mt-2 rounded-lg bg-teal-50 px-3 py-2 text-xs font-semibold text-teal-800 disabled:opacity-50">Confirm appointment</button>}
                  {consultationDataExists && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-2">
                      <div className="text-[11px] font-semibold text-slate-600">Update consultation outcome</div>
                      <select
                        value={consultationOutcome}
                        onChange={(e) => setConsultationOutcome(e.target.value as ConsultationStage)}
                        disabled={busyConsultation || lockedForLeadActions}
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs"
                      >
                        {Object.entries(CONSULTATION_STAGE_LABEL)
                          .map(([stage, label]) => (
                            <option key={stage} value={stage} disabled={stage === 'requested' || stage === 'booked' || stage === 'confirmed'}>
                              {label}
                            </option>
                          ))}
                      </select>
                      {consultationOutcome === 'rescheduled' && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <input type="date" value={rescheduledDate} onChange={(e) => setRescheduledDate(e.target.value)} disabled={busyConsultation || lockedForLeadActions} className="rounded-lg border border-slate-300 px-2 py-2 text-xs" />
                          <input type="time" value={rescheduledTime} onChange={(e) => setRescheduledTime(e.target.value)} disabled={busyConsultation || lockedForLeadActions} className="rounded-lg border border-slate-300 px-2 py-2 text-xs" />
                        </div>
                      )}
                      <button type="button" onClick={updateConsultationStage} disabled={busyConsultation || lockedForLeadActions || !consultationOutcome} className="mt-2 w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
                        {busyConsultation ? 'Saving…' : 'Update outcome'}
                      </button>
                      {lead.consultation_updated_by && <div className="mt-1 text-[10px] text-slate-400">Last updated by {lead.consultation_updated_by}</div>}
                    </div>
                  )}
                </div>
                {lead.raw_answers && lead.source === 'neodove' && neodoveDetails.length > 0 && (
                  <>
                    <div className="mt-2 text-xs text-slate-500">Details</div>
                    <div className="mt-1 rounded-lg bg-white p-2 text-xs">
                      {neodoveDetails.map((row) => (
                        <div key={row.label} className="flex justify-between gap-3 py-1">
                          <span className="text-slate-500">{row.label}</span>
                          <span className="break-words text-right font-medium text-slate-800">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {lead.raw_answers && !(lead.source === 'neodove' && neodoveDetails.length > 0) && (() => {
                  const whatsapp = buildWhatsAppPresentation(lead.raw_answers, {
                    source: lead.source,
                    latestSource: lead.latest_source,
                    sourceHistory: lead.source_history,
                    clinicTag: lead.clinic_tag,
                  });

                  // WhatsApp leads can have only legacy raw-answer history when WATI history is empty.
                  // Keep the composer available for that case too.
                  if (whatsapp) {
                    const legacyMessages: WhatsAppMessageRecord[] = (whatsapp?.messages ?? []).map((m) => ({
                      id: m.messageId || `${m.timestamp}`,
                      provider_message_id: null,
                      direction: m.direction || 'inbound',
                      text: m.message,
                      type: 'text',
                      timestamp: m.timestamp,
                      status: null,
                    }));
                    const merged = reconcileWhatsAppHistory(legacyMessages, {
                      provider: whatsappProvider || 'wati',
                      messages: whatsappMessages,
                    });
                    const livePresentation: WhatsAppPresentation = {
                      messages: merged.map((m) => ({
                        messageId: m.id,
                        direction: m.direction,
                        message: m.text,
                        timestamp: m.timestamp,
                        status: m.status || undefined,
                      })),
                      metadata: whatsapp?.metadata ?? [],
                    };
                    return (
                      <WhatsAppConversationView
                        presentation={livePresentation}
                        composer={
                          <>
                            <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="text-xs font-semibold text-emerald-900">WhatsApp qualifier</div>
                                {conversationAssist?.stage && <span className="rounded-full bg-white px-2 py-1 text-[10px] font-semibold text-emerald-700">{STAGE_LABEL[conversationAssist.stage] ?? conversationAssist.stage}</span>}
                              </div>
                              {conversationAssist ? <>
                                <div className="mt-2 text-xs text-emerald-900">{conversationAssist.summary || conversationAssist.rationale}</div>
                                {conversationAssist.qualification_hint && conversationAssist.qualification_hint !== 'undecided' && (
                                  <div className="mt-1 text-xs font-semibold text-emerald-900">Suggested outcome: {HINT_LABEL[conversationAssist.qualification_hint]}</div>
                                )}
                                <div className="mt-1 text-xs text-emerald-800">Next: {conversationAssist.next_action}</div>
                                <div className="mt-2 rounded-lg bg-white p-2 text-xs text-slate-700">{conversationAssist.suggested_reply}</div>
                                <button type="button" onClick={() => setWaText(conversationAssist.suggested_reply)} className="mt-2 rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white">Insert editable reply</button>
                              </> : <button type="button" onClick={() => void suggestWhatsAppReply()} disabled={assistBusy} className="mt-2 rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">{assistBusy ? 'Reading the chat…' : 'Read this chat'}</button>}
                              <div className="mt-2 text-[10px] text-emerald-700">Suggestion only. Review before sending{conversationAssist?.reply_guarded ? ' · the AI reply broke a rule (price, promise or condition) and was replaced' : ''}.</div>
                            </div>
                            <WhatsAppComposer
                              value={waText}
                              busy={busyWa}
                              disabled={lockedForLeadActions || !contactPolicy.messageAllowed}
                              error={waSendError}
                              onChange={setWaText}
                              onSend={sendSessionMessage}
                              onFile={sendFile}
                            />

                          </>
                        }
                      />
                    );
                  }

                  const rows = formatAnswersForDisplay(lead.raw_answers);
                  if (rows.length === 0) return null;

                  const { visible, hidden } = lead.source === 'meta_form' ? splitMetaRows(rows) : { visible: rows, hidden: [] as AnswerRow[] };
                  const showToggle = lead.source === 'meta_form' && hidden.length > 0;
                  const extraRows = showMetaTechnical ? hidden : [];
                  const cardRows = lead.source === 'meta_form' ? visible : rows;

                  return (
                    <>
                      <div className="mt-2 text-xs text-slate-500">Details</div>
                      <AnswersCard rows={cardRows} />
                      {showToggle && (
                        <button
                          type="button"
                          onClick={() => setShowMetaTechnical((v) => !v)}
                          className="mt-2 text-xs font-semibold text-slate-600 underline decoration-slate-300 underline-offset-2 hover:text-slate-900"
                        >
                          {showMetaTechnical ? 'Show less' : 'Show more'}
                        </button>
                      )}
                      {showToggle && showMetaTechnical && extraRows.length > 0 && (
                        <div className="mt-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-2">
                          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                            Technical details
                          </div>
                          <AnswersCard rows={extraRows} />
                        </div>
                      )}
                    </>
                  );
                })()}
              </section>

              <section className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      System attribution
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-500">
                      Captured automatically. Staff notes do not overwrite these fields.
                    </div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${attributionState.className}`}>
                    {attributionState.label}
                  </span>
                </div>
                {/* Technical IDs are for managers; telecallers see the plain-words source in "What to know". */}
                {!isTelecaller && <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <div><div className="text-slate-500">Platform/channel</div><div className="break-words font-medium text-slate-800">{[attribution?.platform, attribution?.channel].filter(Boolean).join(' · ') || '—'}</div></div>
                  <div><div className="text-slate-500">Evidence</div><div className="break-words font-medium text-slate-800">{attribution?.resolved_via || '—'}</div></div>
                  <div className="col-span-2"><div className="text-slate-500">Campaign</div><div className="break-all font-medium text-slate-800">{attribution?.campaign_name || attribution?.campaign_id || '—'}</div>{attribution?.campaign_name && attribution?.campaign_id && <div className="break-all text-[10px] text-slate-500">{attribution.campaign_id}</div>}</div>
                  <div><div className="text-slate-500">Ad set ID</div><div className="break-all font-medium text-slate-800">{attribution?.adset_id || '—'}</div></div>
                  <div><div className="text-slate-500">Ad</div><div className="break-all font-medium text-slate-800">{attribution?.ad_name || attribution?.ad_id || '—'}</div>{attribution?.ad_name && attribution?.ad_id && <div className="break-all text-[10px] text-slate-500">{attribution.ad_id}</div>}</div>
                  <div><div className="text-slate-500">Meta referral</div><div className="break-all font-medium text-slate-800">{attribution?.ctwa_clid || attribution?.referral_source_id || '—'}</div></div>
                  <div><div className="text-slate-500">Provider message</div><div className="break-all font-medium text-slate-800">{attribution?.provider_message_id || '—'}</div></div>
                </div>}
                <div className="mt-3 border-t border-slate-200 pt-3">
                  <div className="text-xs font-semibold text-slate-700">Patient-reported origin</div>
                  <div className="mt-0.5 text-[10px] text-slate-500">
                    Ask only when automatic attribution is missing or the contact is direct. This is self-reported evidence—not a campaign ID.
                  </div>
                  <select
                    value={reportedSourceCategory}
                    onChange={(event) => setReportedSourceCategory(event.target.value as LeadReportedSource['category'] | '')}
                    disabled={busyReportedSource || lockedForLeadActions}
                    className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs"
                  >
                    <option value="">Select what the patient says</option>
                    {REPORTED_SOURCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {(reportedSourceCategory === 'other' || reportedSourceDetail) && (
                    <input
                      value={reportedSourceDetail}
                      onChange={(event) => setReportedSourceDetail(event.target.value)}
                      disabled={busyReportedSource || lockedForLeadActions}
                      maxLength={500}
                      placeholder="Short detail (required for Other)"
                      className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs"
                    />
                  )}
                  <button
                    type="button"
                    onClick={saveReportedSource}
                    disabled={busyReportedSource || lockedForLeadActions || !reportedSourceCategory}
                    className="mt-2 w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                  >
                    {busyReportedSource ? 'Saving…' : 'Save patient-reported origin'}
                  </button>
                  {reportedSource && (
                    <div className="mt-1 text-[10px] text-slate-400">
                      Latest entry by {reportedSource.actor.split('@')[0]}{reportedSource.created_at ? ` · ${historyValue(reportedSource.created_at, true)}` : ''}
                    </div>
                  )}
                </div>
              </section>

                <section className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Lead History
                  </div>
                  {!!lead.returned_lead && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                      Returned Lead
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <div className="col-span-2">
                    <div className="text-slate-500">Name</div>
                    <div className="font-medium text-slate-800">{historyValue(lead.name)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Original source</div>
                    <div className="font-medium text-slate-800">{historySourceLabel(historyValue(lead.source))}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Latest source</div>
                    <div className="font-medium text-slate-800">{historySourceLabel(historyValue(lead.latest_source || lead.source))}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-slate-500">All sources</div>
                    <div className="font-medium text-slate-800">
                      {sourceHistory(lead.source_history).map(historySourceLabel).join(', ') || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-500">Previous agent</div>
                    <div className="font-medium text-slate-800">{historyValue(lead.last_assigned_to || (lead.returned_lead ? lead.assigned_to : null))}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Previous status</div>
                    <div className="font-medium text-slate-800">{historyValue(lead.returned_from_status)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Lost date</div>
                    <div className="font-medium text-slate-800">{historyValue(lead.returned_from_lost_at, true)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Lost reason</div>
                    <div className="font-medium text-slate-800">{historyValue(lead.returned_from_lost_reason)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Returned date</div>
                    <div className="font-medium text-slate-800">{historyValue(lead.returned_at, true)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Returned source</div>
                    <div className="font-medium text-slate-800">{lead.returned_lead ? historySourceLabel(historyValue(lead.latest_source || lead.source)) : '—'}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-slate-500">Agent notes</div>
                    {agentNotes.length === 0 ? (
                      <div className="font-medium text-slate-800">—</div>
                    ) : (
                      <ul className="mt-1 space-y-1.5">
                        {agentNotes.map((note, index) => (
                          <li key={`${note.createdAt}-${index}`} className="rounded-lg bg-slate-50 px-2.5 py-2">
                            <div className="whitespace-pre-wrap break-words font-medium text-slate-800">{note.note}</div>
                            <div className="mt-1 text-[10px] text-slate-500">
                              {note.actor ? note.actor.split('@')[0] : 'Agent'} · {historyValue(note.createdAt, true)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  What to know · what to say
                </div>
                {!contactPolicy.guidance && lead.persuasion_tip && <p className="mb-2 text-xs font-medium text-slate-700">{lead.persuasion_tip}</p>}
                <div className="rounded-2xl rounded-tl-sm bg-wechat-bubble px-3 py-2 text-sm">
                  {contactPolicy.guidance || lead.opening_line}
                </div>
              </section>

              <section>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Assignment
                  </div>
                </div>
                {isTelecaller ? (
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">
                        {mine ? 'Assigned to you' : lead.assigned_to ? 'Assigned to someone else' : 'Not assigned yet'}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {mine
                          ? 'You can use all lead actions.'
                          : lead.assigned_to
                          ? 'This lead is locked to another telecaller.'
                          : 'You can view it, then claim it to unlock actions.'}
                      </div>
                      {!lead.assigned_to && previousAssignee && (
                        <div className="mt-1 text-[11px] text-slate-500">
                          Previously handled by <span className="font-semibold text-slate-700">{previousAssignee}</span>
                        </div>
                      )}
                    </div>
                    {!mine && !lead.assigned_to ? (
                      <button
                        onClick={() => assign(me)}
                        disabled={busy}
                        className="flex-none rounded-full bg-emerald-100 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 disabled:opacity-60"
                      >
                        Assign to me
                      </button>
                    ) : !mine && lead.assigned_to ? (
                      <span className="flex-none rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-semibold text-slate-600">
                        No access
                      </span>
                    ) : (
                      <span className="flex-none rounded-full bg-emerald-100 px-3 py-1.5 text-[11px] font-semibold text-emerald-700">
                        Assigned
                      </span>
                    )}
                  </div>
                ) : (
                  <select
                    value={lead.assigned_to ?? ''}
                    onChange={(e) => assign(e.target.value || null)}
                    disabled={busy}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">Unassigned</option>
                    {isManager && (
                      <option value={me}>Assign to me ({me})</option>
                    )}
                    {telecallers.map((t) => (
                      <option key={t.email} value={t.email}>
                        {t.name ? `${t.name} (${t.email})` : t.email}
                      </option>
                    ))}
                    {lead.assigned_to && !telecallers.find((t) => t.email === lead.assigned_to) && (
                      <option value={lead.assigned_to}>{lead.assigned_to}</option>
                    )}
                  </select>
                )}
              </section>

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Status
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {STATUSES.map((s) => {
                    const active = s === lead.status;
                    return (
                      <button
                        key={s}
                        onClick={() => changeStatus(s)}
                        disabled={busy || (active && !['qualified', 'contacted', 'lost'].includes(s)) || lockedForLeadActions}
                        className={`rounded-full px-3 py-1 text-xs font-semibold disabled:opacity-60 ${
                          active ? STATUS_COLOR[s] : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {STATUS_LABEL[s]}
                      </button>
                    );
                  })}
                </div>
              </section>

              {registrationOpen && <RegistrationModal leadId={lead.id} onClose={() => setRegistrationOpen(false)} onRecorded={async () => { await load(); onChanged(); }} />}

              {wonReasonOpen && (
                <WonReasonModal
                  open={wonReasonOpen}
                  busy={busy}
                  consultationDate={consultationDate}
                  consultationTime={consultationTime}
                  consultationMode={consultationMode}
                  consultationLocation={consultationLocation}
                  confirmationSource={confirmationSource}
                  confirmationReference={confirmationReference}
                  consultationError={consultationError}
                  onConsultationDateChange={(value) => { setConsultationDate(value); setConsultationError(''); }}
                  onConsultationTimeChange={(value) => { setConsultationTime(value); setConsultationError(''); }}
                  onConsultationModeChange={(value) => { setConsultationMode(value); setConsultationError(''); }}
                  onConsultationLocationChange={(value) => { setConsultationLocation(value); setConsultationError(''); }}
                  onConfirmationSourceChange={(value) => { setConfirmationSource(value); setConsultationError(''); }}
                  onConfirmationReferenceChange={(value) => { setConfirmationReference(value); setConsultationError(''); }}
                  onCancel={() => {
                    if (!busy) {
                      setWonReasonOpen(false);
                      setWonReasonCode('');
                      setWonReasonText('');
                      setScheduleConsultation(false);
                      setConsultationDate('');
                      setConsultationTime('');
                      setConsultationMode('');
                      setConsultationLocation('');
                      setConfirmationSource('');
                      setConfirmationReference('');
                      setConsultationError('');
                    }
                  }}
                  onConfirm={confirmWon}
                />
              )}

              {dispositionReasonOpen && (
                <ReasonPickerModal
                  open={dispositionReasonOpen}
                  busy={busy}
                  selectedCode={dispositionReasonCode}
                  customReason={dispositionReasonText}
                  title={
                    dispositionReasonStatus === 'contacted'
                      ? 'Record the contact result'
                      : dispositionReasonStatus === 'invalid_lead' ? 'Why is this enquiry invalid?' : 'Why did this opportunity not proceed?'
                  }
                  confirmLabel={
                    dispositionReasonStatus === 'contacted' ? 'Record contact attempt' : dispositionReasonStatus === 'invalid_lead' ? 'Mark Rejected' : 'Mark Lost'
                  }
                  confirmTone="bg-rose-600"
                  options={dispositionReasonStatus === 'contacted' ? CONTACTED_REASONS : dispositionReasonStatus === 'invalid_lead' ? REJECTED_REASONS : LOST_REASONS}
                  onSelect={setDispositionReasonCode}
                  onCustomReasonChange={setDispositionReasonText}
                  onCancel={() => {
                    if (!busy) {
                      setDispositionReasonOpen(false);
                      setDispositionReasonStatus('');
                      setDispositionReasonCode('');
                      setDispositionReasonText('');
                    }
                  }}
                  onConfirm={confirmDisposition}
                />
              )}

              {qualifiedReasonOpen && (
                <QualifiedReasonModal
                  open={qualifiedReasonOpen}
                  busy={busy}
                  selectedCategory={qualifiedReasonCategory}
                  selectedCode={qualifiedReasonCode}
                  title="Please specify the qualified reason"
                  confirmLabel="Mark Qualified"
                  confirmTone="bg-emerald-600"
                  onSelectCategory={setQualifiedReasonCategory}
                  onSelectReason={setQualifiedReasonCode}
                  onCancel={() => {
                    if (!busy) {
                      setQualifiedReasonOpen(false);
                      setQualifiedReasonCategory('');
                      setQualifiedReasonCode('');
                      setQualificationOutcomeChoice('');
                      setQualificationOutcomeReason('');
                      setQualificationNextActionDate('');
                      setQualificationNextActionTime('');
                    }
                  }}
                  onConfirm={confirmQualifiedReason}
                  qualificationOutcome={qualificationOutcomeChoice}
                  qualificationOutcomeReason={qualificationOutcomeReason}
                  nextActionDate={qualificationNextActionDate}
                  nextActionTime={qualificationNextActionTime}
                  consultationDate={consultationDate}
                  consultationTime={consultationTime}
                  consultationMode={consultationMode}
                  consultationLocation={consultationLocation}
                  qualifiedCenter={qualifiedCenter}
                  availableSlots={availableSlots}
                  availabilityLoading={availabilityLoading}
                  availabilityError={availabilityError}
                  onRetryAvailability={() => setAvailabilityRefresh((value) => value + 1)}
                  consultationError={consultationError}
                  onQualificationOutcome={(value) => {
                    setQualificationOutcomeChoice(value);
                    setScheduleConsultation(value === 'booked');
                    setConsultationTime('');
                    setConsultationError('');
                    if ((value === 'positive' || value === 'pending') && !qualificationNextActionDate) {
                      // Default follow-up: two days later at 11:00 (IST, the device's local time in Odisha).
                      const inTwoDays = new Date(Date.now() + 2 * 86_400_000);
                      setQualificationNextActionDate(`${inTwoDays.getFullYear()}-${String(inTwoDays.getMonth() + 1).padStart(2, '0')}-${String(inTwoDays.getDate()).padStart(2, '0')}`);
                      setQualificationNextActionTime('11:00');
                    }
                  }}
                  onQualificationOutcomeReason={setQualificationOutcomeReason}
                  onNextActionDate={setQualificationNextActionDate}
                  onNextActionTime={setQualificationNextActionTime}
                  onConsultationDate={(value) => { setConsultationDate(value); setConsultationTime(''); setConsultationError(''); }}
                  onConsultationTime={(value) => { setConsultationTime(value); setConsultationError(''); }}
                  onConsultationMode={(value) => { setConsultationMode(value); setConsultationTime(''); setConsultationError(''); }}
                  onConsultationLocation={(value) => { setConsultationLocation(value); setConsultationError(''); }}
                  onQualifiedCenter={(value) => { setQualifiedCenter(value); setConsultationTime(''); }}
                />
              )}

              <PhonePromptModal
                open={phonePromptOpen}
                busy={busy}
                value={phoneDraft}
                onChange={setPhoneDraft}
                onCancel={() => {
                  if (!busy) setPhonePromptOpen(false);
                }}
                onConfirm={savePromptPhone}
              />

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Lead Tier (Hot/Warm/Cold)
                </div>
                {parseLeadTags(lead.tags).includes('extra_hot') && (
                  <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
                    🔥🔥 Extra Hot — Social DM lead
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {[1, 2, 3].map((t) => {
                    const active = t === lead.tier_score;
                    const tierLabels: Record<number, string> = {
                      1: '🔥 Hot',
                      2: '🌡️ Warm',
                      3: '❄️ Cold',
                    };
                    const tierColors: Record<number, string> = {
                      1: 'bg-red-100 text-red-800',
                      2: 'bg-amber-100 text-amber-800',
                      3: 'bg-blue-100 text-blue-800',
                    };
                    return (
                      <button
                        key={t}
                        onClick={() => changeTier(t as 1 | 2 | 3)}
                        disabled={busy || active || lockedForLeadActions}
                        className={`rounded-full px-3 py-1 text-xs font-semibold disabled:opacity-60 ${
                          active ? tierColors[t] : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {tierLabels[t]}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-[11px] italic text-slate-600">
                  Tier 1 = Hot/Urgent, Tier 2 = Warm/Informational, Tier 3 = Cold/Low priority
                </p>
              </section>

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Call
                </div>
                <input
                  value={phone}
                  onChange={(e) => savePhone(e.target.value)}
                  placeholder="Your phone (E.164, +9198…)"
                  inputMode="tel"
                  disabled={lockedForLeadActions}
                  className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
                />
                <div className="flex gap-2">
                  <a
                    href={lockedForLeadActions || !contactPolicy.messageAllowed ? undefined : waHref(lead.phone, lead.opening_line)}
                    aria-disabled={lockedForLeadActions || !contactPolicy.messageAllowed}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => {
                      if (lockedForLeadActions || !contactPolicy.messageAllowed) {
                        e.preventDefault();
                        if (!blockIfLocked()) setActionNotice(contactPolicy.guidance);
                      }
                    }}
                    className={`flex-1 rounded-lg px-3 py-2 text-center text-sm font-semibold text-white active:scale-[0.98] ${
                      lockedForLeadActions || !contactPolicy.messageAllowed ? 'cursor-not-allowed bg-slate-400' : 'bg-wechat-accent'
                    }`}
                  >
                    Open WhatsApp
                  </a>
                  <button
                    onClick={call}
                    disabled={busyCall || lockedForLeadActions}
                    className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60 active:scale-[0.98]"
                  >
                    {busyCall ? 'Dialing…' : 'Call via Vobiz'}
                  </button>
                </div>
                {contactPolicy.guidance && <p className="mt-2 text-xs text-amber-800" role="status">{contactPolicy.guidance}</p>}
                {callResult && <CallResultNotice result={callResult} />}
              </section>

              <section>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    WhatsApp template
                  </div>
                  <span className="text-[10px] text-slate-400">
                    {templates.filter((t) => t.status === 'APPROVED').length} approved ·{' '}
                    {templates.filter((t) => t.status === 'PENDING').length} pending
                  </span>
                </div>
                  <select
                    value={selectedTpl}
                    onChange={(e) => pickTemplate(e.target.value)}
                    disabled={lockedForLeadActions}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100"
                  >
                  <option value="">— pick a template —</option>
                  <optgroup label="Approved">
                    {templates
                      .filter((t) => t.status === 'APPROVED')
                      .map((t) => (
                        <option key={t.name} value={t.name}>
                          {t.label} · {t.category}
                          {t.params.length ? ` · ${t.params.length}p` : ''}
                        </option>
                      ))}
                  </optgroup>
                  <optgroup label="Pending Meta approval">
                    {templates
                      .filter((t) => t.status === 'PENDING')
                      .map((t) => (
                        <option key={t.name} value={t.name} disabled>
                          {t.label} · {t.category} · PENDING
                        </option>
                      ))}
                  </optgroup>
                </select>

                {currentTemplate && (
                  <>
                    {currentTemplate.note && (
                      <p className="mt-1.5 text-[11px] italic text-amber-700">
                        ⚠ {currentTemplate.note}
                      </p>
                    )}
                    {currentTemplate.params.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {currentTemplate.params.map((label, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="w-5 flex-none text-[11px] font-mono text-slate-500">
                              {`{{${i + 1}}}`}
                            </span>
                            <input
                              value={tplParams[i] ?? ''}
                              onChange={(e) => {
                                const next = [...tplParams];
                                next[i] = e.target.value;
                                setTplParams(next);
                              }}
                              placeholder={label}
                              disabled={lockedForLeadActions}
                              className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs disabled:bg-slate-100"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={sendTemplate}
                      disabled={
                        busyWa ||
                        currentTemplate.status !== 'APPROVED' ||
                        currentTemplate.params.some((_, i) => !tplParams[i]?.trim()) ||
                        lockedForLeadActions || !contactPolicy.messageAllowed
                      }
                      className="mt-2 w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 active:scale-[0.98]"
                    >
                      {busyWa ? 'Sending…' : `Send ${currentTemplate.name} → ${lead.phone}`}
                    </button>
                  </>
                )}
              </section>

              {calls.length > 0 && (
                <section>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Call logs ({calls.length})
                  </div>
                  <ul className="space-y-1.5">
                    {calls.map((cl) => (
                      <li
                        key={cl.id}
                        className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs"
                      >
                        <span className="text-slate-700">
                          {cl.created_at}
                          {cl.initiated_by && (
                            <span className="ml-1 text-slate-500">
                              · {cl.initiated_by.split('@')[0]}
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="font-semibold">{fmtDuration(cl.duration)}</span>
                          {cl.recording_url && (
                            <a
                              href={cl.recording_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 underline"
                            >
                              recording
                            </a>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Follow-ups
                  </div>
                  <button
                    onClick={() => setShowFollowupForm(!showFollowupForm)}
                    disabled={lockedForLeadActions || !contactPolicy.messageAllowed}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-60"
                  >
                    {showFollowupForm ? 'Cancel' : '+ Schedule'}
                  </button>
                </div>

                <div className="mb-3">
                  <div className="mb-1.5 text-[10px] uppercase tracking-wider text-slate-400">
                    Quick follow-up at 10:00 AM
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {quickFollowupDays.map((days) => (
                      <button
                        key={days}
                        onClick={() => scheduleQuickFollowup(days)}
                        disabled={busyFollowup || lockedForLeadActions || !contactPolicy.messageAllowed}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {days} {days === 1 ? 'day' : 'days'}
                      </button>
                    ))}
                  </div>
                </div>

                {showFollowupForm && (
                  <div className="mb-3 rounded-lg bg-slate-50 p-3 space-y-2">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Date *
                      </label>
                      <input
                        type="date"
                        value={followupDate}
                        onChange={(e) => setFollowupDate(e.target.value)}
                        disabled={lockedForLeadActions}
                        className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Time (optional)
                      </label>
                      <input
                        type="time"
                        value={followupTime}
                        onChange={(e) => setFollowupTime(e.target.value)}
                        disabled={lockedForLeadActions}
                        className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={followupNotes}
                        onChange={(e) => setFollowupNotes(e.target.value)}
                        placeholder="What did you discuss? What to follow up on?"
                        rows={2}
                        disabled={lockedForLeadActions}
                        className="w-full resize-none rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      />
                    </div>
                    <button
                      onClick={handleCreateFollowup}
                      disabled={busyFollowup || !followupDate || lockedForLeadActions || !contactPolicy.messageAllowed}
                      className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 active:scale-[0.98]"
                    >
                      {busyFollowup ? 'Scheduling…' : 'Schedule Follow-up'}
                    </button>
                  </div>
                )}

                {followups.length === 0 && !showFollowupForm && (
                  <p className="text-xs text-slate-500">No follow-ups scheduled.</p>
                )}

                {followups.length > 0 && (
                  <ul className="space-y-1.5">
                    {followups.map((fu) => (
                      <li
                        key={fu.id}
                        className={`rounded-lg px-3 py-2 text-xs ${
                          fu.status === 'completed'
                            ? 'bg-emerald-50 border border-emerald-200'
                            : fu.status === 'cancelled'
                            ? 'bg-slate-100 border border-slate-200 opacity-60'
                            : 'bg-amber-50 border border-amber-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-slate-800">
                              {fu.followup_date}
                              {fu.followup_time && ` at ${fu.followup_time}`}
                            </div>
                            {fu.notes && (
                              <div className="mt-1 text-slate-600">{fu.notes}</div>
                            )}
                            <div className="mt-1 text-[10px] text-slate-500">
                              {fu.created_by.split('@')[0]} · {fu.created_at}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                fu.status === 'completed'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : fu.status === 'cancelled'
                                  ? 'bg-slate-200 text-slate-600'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {fu.status}
                            </span>
                            {fu.status === 'pending' && (
                              <button
                                onClick={() => handleCompleteFollowup(fu.id)}
                                disabled={lockedForLeadActions}
                                className="rounded bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                              >
                                Complete
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Notes
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add call reason, state, promise, objection, or anything important..."
                    rows={3}
                    disabled={lockedForLeadActions}
                    className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-100"
                  />
                  <button
                    onClick={addNote}
                    disabled={busyNote || !noteText.trim() || lockedForLeadActions}
                    className="mt-2 w-full rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 active:scale-[0.98]"
                  >
                    {busyNote ? 'Adding…' : 'Add note'}
                  </button>
                </div>
              </section>

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Timeline
                </div>
                {events.length === 0 ? (
                  <p className="text-xs text-slate-500">No events yet.</p>
                ) : (
                  <ul className="space-y-2 border-l border-slate-200 pl-3">
                    {events.map((ev) => {
                      let dataObj: Record<string, unknown> = {};
                      try {
                        dataObj = ev.data ? JSON.parse(ev.data) : {};
                      } catch {}
                      return (
                        <li key={ev.id} className="relative text-xs">
                          <span className="absolute -left-[17px] top-1 h-2 w-2 rounded-full bg-slate-400" />
                          <div className="font-medium text-slate-800">{eventLabel(ev.kind)}</div>
                          <div className="text-slate-500">
                            {formatDateTime(ev.created_at)}
                            {ev.actor && ev.actor !== 'system' && (
                              <span className="ml-1">· {ev.actor.split('@')[0]}</span>
                            )}
                          </div>
                          {Object.keys(dataObj).length > 0 && (
                            <div className="mt-0.5 text-slate-600">
                              {Object.entries(dataObj)
                                .filter(([, v]) => v !== '' && v !== null && v !== undefined)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(' · ')}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </>
          )}
      </div>
      {actionNotice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4" role="presentation">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="lead-action-message-title"
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-xl text-blue-700" aria-hidden="true">
              i
            </div>
            <h3 id="lead-action-message-title" className="text-lg font-semibold text-slate-900">Please note</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">{actionNotice}</p>
            <button
              type="button"
              autoFocus
              onClick={() => setActionNotice(null)}
              className="mt-5 w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white active:bg-blue-700"
            >
              Okay
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (inline) {
    return <div className="flex h-full flex-col bg-white">{detailInner}</div>;
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/40" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute inset-x-0 bottom-0 max-h-[92vh] overflow-hidden rounded-t-3xl bg-white shadow-2xl"
      >
        {detailInner}
      </div>
    </div>
  );
}
